import { useEffect, useMemo, useState, useCallback, useRef } from "react";
import { toast } from "react-toastify";
import { parseDurationSec as parseTimeLimitSeconds, formatTime } from "../../utils/time";
import { useNavigate, useLocation } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Grid,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Typography,
  LinearProgress,
  Paper,
  Chip,
  Divider,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Tooltip,
  Menu,
  Collapse,
  Stepper,
  Step,
  StepLabel,
  CircularProgress,
  Skeleton,
  TextField,
  InputAdornment,
  Tabs,
  Tab,
  Checkbox,
  ListItemText,
  Radio,
  RadioGroup,
  FormControlLabel,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import DownloadIcon from "@mui/icons-material/Download";
import AssignmentTurnedInIcon from "@mui/icons-material/AssignmentTurnedIn";
import FactCheckIcon from "@mui/icons-material/FactCheck";
import BarChartIcon from "@mui/icons-material/BarChart";
import QuizIcon from "@mui/icons-material/Quiz";
import CloseIcon from "@mui/icons-material/Close";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import AutorenewIcon from "@mui/icons-material/Autorenew";
import FlagIcon from "@mui/icons-material/Flag";
import SearchIcon from "@mui/icons-material/Search";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import SchoolIcon from "@mui/icons-material/School";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import PauseIcon from "@mui/icons-material/Pause";
import PlayCircleOutlineIcon from "@mui/icons-material/PlayCircleOutline";
import RestoreOutlinedIcon from "@mui/icons-material/RestoreOutlined";
import api from "../../api";
import useExamMode from "../../hooks/useExamMode";
import VideoExplanationPlayer from "../../components/VideoExplanationPlayer";
import HtmlWithLatex from "../QuestionBank/components/HtmlWithLatex";
import { sortHierarchyItems } from "../../utils/hierarchyOrdering";
import useContentVerification from "../../hooks/useContentVerification";
import { testerHasPermission } from "../../utils/testerPermissions";
import VerifiedUserIcon from "@mui/icons-material/VerifiedUser";

// Upload progress stages
const UPLOAD_STAGES = [
  { key: "uploading", label: "Uploading File", icon: CloudUploadIcon },
  { key: "processing", label: "Reading Answers", icon: FactCheckIcon },
  { key: "grading", label: "Evaluating", icon: AssignmentTurnedInIcon },
  { key: "explanations", label: "Generating Explanations", icon: AutorenewIcon },
  { key: "complete", label: "Complete", icon: CheckCircleIcon },
];

const TAB_WORKSHEETS = 0;
const TAB_RESULTS = 1;
const RESULT_VIEW_SCORE = "score";
const PENDING_UPLOAD_KEY = "ai_upload_pending_session";
const RESULT_VIEW_TOPIC = "topic";
const RESULT_VIEW_QUESTIONS = "questions";

/** Avoid "week week 1" when API omits week_label but week_number is already "week 1". */
const formatWeekOptionFallback = (weekNumber) => {
  const s = String(weekNumber ?? "").trim();
  if (!s) return "";
  if (/^week\s/i.test(s)) return s.toLowerCase();
  if (/^\d+$/.test(s)) return `week ${s}`;
  return s.toLowerCase();
};

const DIFFICULTY_LEVELS = [
  { value: "Easy", chilies: 1, color: "#1f9d55", bg: "#e6f7ec" },
  { value: "Medium", chilies: 2, color: "#c9821a", bg: "#fff4e0" },
  { value: "Hard", chilies: 3, color: "#d64545", bg: "#fde8e8" },
];

const normalizeDifficulty = (d) => {
  const s = String(d ?? "").trim().toLowerCase();
  const found = DIFFICULTY_LEVELS.find((lvl) => lvl.value.toLowerCase() === s);
  return found ? found.value : null;
};

const isValidTestId = (id) => {
  if (id == null || id === '' || id === 'null' || id === 'undefined') return false;
  const n = Number(id);
  return Number.isFinite(n) && n > 0;
};

const AiAnswerSheetUpload = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobileViewport = useMediaQuery(theme.breakpoints.down("sm"));
  const role = (localStorage.getItem("role") || "").toLowerCase();
  const isTesterAI = role === "tester";
  const userData = (() => { try { return JSON.parse(localStorage.getItem("userData") || "{}"); } catch { return {}; } })();
  // Mobile-subscriber students browse by subject/topic and have no course enrolment — hide Course/Week on any viewport.
  const isMobileRestrictedStudent = role === "student" && !!userData.is_allow_mobile;
  // Mobile viewers don't need the Week filter clutter; testers always keep it for QA.
  const showWeekFilter = isTesterAI || (!isMobileViewport && !isMobileRestrictedStudent);
  // Mobile viewers don't need the Course filter either — they browse by subject/topic.
  const showCourseFilter = isTesterAI || (!isMobileViewport && !isMobileRestrictedStudent);
  const parentPrefix = `/${process.env.REACT_APP_PARENT_PREFIX || "parent"}`;
  const isParentPortal = location.pathname.startsWith(parentPrefix);
  const isParent = role === "parent" || (isTesterAI && isParentPortal);
  const prefix = isParent ? "parent" : "student";
  const canDownloadAnswerKey = isParent || isTesterAI;
  const [showPracticeResults, setShowPracticeResults] = useState(false);
  const canVerifyWorksheet = isTesterAI && testerHasPermission("verify-worksheet");
  const { loadVerifications: loadWSSheetVerifications, submitVerification: submitWSSheetVerification, getItemData: getWSSheetItemData, submitting: wsSheetVerifySubmitting } = useContentVerification();
  const [wsSheetVerifyDialogOpen, setWsSheetVerifyDialogOpen] = useState(false);
  const [wsVerifyStatus, setWsVerifyStatus] = useState("approved");
  const [wsVerifyNotes, setWsVerifyNotes] = useState("");
  const [tab, setTab] = useState(() => (isParent ? TAB_RESULTS : TAB_WORKSHEETS));
  const [resultViewTab, setResultViewTab] = useState(RESULT_VIEW_SCORE);
  const [resultTestMeta, setResultTestMeta] = useState(null);
  const [allAttempts, setAllAttempts] = useState([]);
  const [loadingAllAttempts, setLoadingAllAttempts] = useState(false);
  const [attemptsPage, setAttemptsPage] = useState(1);
  const [attemptsPagination, setAttemptsPagination] = useState({ current_page: 1, last_page: 1, total: 0 });
  const RETAKE_PAGE_SIZE = 1;
  const EVALUATION_PAGE_SIZE = 1;
  const [students, setStudents] = useState([]);
  const [selectedStudentId, setSelectedStudentId] = useState("");
  const { isTestingMode } = useExamMode(selectedStudentId || null);
  const isTestMode = isTestingMode("worksheets");
  const [tests, setTests] = useState([]);
  const [selectedTestId, setSelectedTestId] = useState("");
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [fetchingTests, setFetchingTests] = useState(false);

  useEffect(() => {
    const handleExamModeChanged = () => {
      window.location.reload();
    };
    window.addEventListener("exam-mode-changed", handleExamModeChanged);
    return () => window.removeEventListener("exam-mode-changed", handleExamModeChanged);
  }, []);

  useEffect(() => {
    if (isTesterAI) {
      setAllowAnswerKeyDownload(true);
      return;
    }
    if (!isParent) return;
    api.get("parent/settings")
      .then((res) => setAllowAnswerKeyDownload(res.data?.data?.allow_answer_key_download !== false))
      .catch(() => setAllowAnswerKeyDownload(true));
  }, [isParent, isTesterAI]);
  const [fetchTestsError, setFetchTestsError] = useState(false);
  const [result, setResult] = useState(null);
  const [attempts, setAttempts] = useState([]);
  const [loadingAttempts, setLoadingAttempts] = useState(false);
  const [loadingRetakeHistory, setLoadingRetakeHistory] = useState(false);
  const [retakeLoading, setRetakeLoading] = useState({});
  const [retakeSession, setRetakeSession] = useState(null);
  const [retakeAnswers, setRetakeAnswers] = useState({});
  const [submittingRetake, setSubmittingRetake] = useState(false);
  const [retakeResult, setRetakeResult] = useState(null);
  const [retakeHistory, setRetakeHistory] = useState(null);
  const [swapeTotalSeconds, setSwapeTotalSeconds] = useState(0);
  const [swapeTotalRemaining, setSwapeTotalRemaining] = useState(0);
  const [swapeAutoSubmitted, setSwapeAutoSubmitted] = useState(false);
  const [retakePage, setRetakePage] = useState(1);
  const [evaluationPage, setEvaluationPage] = useState(1);
  const [selectedSwapeSession, setSelectedSwapeSession] = useState(null);
  const [swapeSessionModalOpen, setSwapeSessionModalOpen] = useState(false);
  const [actionMenuAnchor, setActionMenuAnchor] = useState({});
  const [hideCorrectAnswers, setHideCorrectAnswers] = useState(!isParent);
  const [activeSwapeMeta, setActiveSwapeMeta] = useState(null);
  const [swapeConfirmOpen, setSwapeConfirmOpen] = useState(false);
  const [pendingSwapeAttempt, setPendingSwapeAttempt] = useState(null);
  const [flaggingQuestion, setFlaggingQuestion] = useState({});
  const [flaggedQuestions, setFlaggedQuestions] = useState(new Set());
  const [imageErrors, setImageErrors] = useState(new Set());
  const [imageLoading, setImageLoading] = useState(new Set());
  const [imageRetries, setImageRetries] = useState(new Map());
  const [uploadProgress, setUploadProgress] = useState(0);
  const [processingStage, setProcessingStage] = useState(null);
  const [validating, setValidating] = useState(false);
  const [validationWarning, setValidationWarning] = useState(null); // {issues, testName, method, navigateToReview, warnOnly}
  const [validationOk, setValidationOk] = useState(false); // brief green flash on QR match
  const aiTestUploadBasePath = isParent ? "/parent/ai-tests" : "/student/ai-tests";
  const aiTestCatalogPath = aiTestUploadBasePath;
  const [selectedCourseId, setSelectedCourseId] = useState("");
  const [selectedSubjectId, setSelectedSubjectId] = useState("");
  const [selectedTopics, setSelectedTopics] = useState([]);
  const [selectedWeekId, setSelectedWeekId] = useState("");
  const [allWeeks, setAllWeeks] = useState([]);
  const [selectedDifficulties, setSelectedDifficulties] = useState([]);
  const [cardSearch, setCardSearch] = useState("");
  const [worksheetPage, setWorksheetPage] = useState(0);
  const [worksheetRowsPerPage, setWorksheetRowsPerPage] = useState(10);
  const [resultsSearch, setResultsSearch] = useState("");
  const [resultsTestFilter, setResultsTestFilter] = useState("");
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [downloadingKey, setDownloadingKey] = useState({});
  const [downloadMenuAnchor, setDownloadMenuAnchor] = useState({});
  const [allowAnswerKeyDownload, setAllowAnswerKeyDownload] = useState(true);
  const [showPreviousAttempts, setShowPreviousAttempts] = useState(false);
  const [showSweepTracker, setShowSweepTracker] = useState(false);
  const [showEvaluationSummary, setShowEvaluationSummary] = useState(false);
  // Ref to hold a pending auto-sweep attempt ID that arrives via navigation state.
  // Using a ref avoids race conditions with the selectedTestId-reset useEffect.
  const pendingAutoSweepRef = useRef(null);

  // Polling pause/resume state
  const [pollingPaused, setPollingPaused] = useState(false);
  const pollingPausedRef = useRef(false);
  const [pollingResultId, setPollingResultId] = useState(null);
  const [pendingMonitorSession, setPendingMonitorSession] = useState(null);

  // AI semantic grading polling state
  const [aiGradingStatus, setAiGradingStatus] = useState(null); // null|pending|processing|completed|failed
  const aiGradingPollRef = useRef(null);
  const aiGradingPollCountRef = useRef(0);

  // ─── computed ────────────────────────────────────────────────────────────
  const courseOptions = useMemo(() => {
    const seen = new Map();
    (tests || []).forEach((t) => {
      if (t.course_id == null || t.course_id === "") return;
      const id = String(t.course_id);
      if (seen.has(id)) return;
      seen.set(id, { id: t.course_id, name: t.course_name || "Unassigned" });
    });
    return sortHierarchyItems(Array.from(seen.values()));
  }, [tests]);

  const subjectKeyForTest = useCallback((t) => {
    if (t?.subject_id != null && t.subject_id !== "") return String(t.subject_id);
    return `name:${t?.subject || ""}`;
  }, []);

  const subjectOptions = useMemo(() => {
    const seen = new Map();
    const base = selectedCourseId
      ? (tests || []).filter((t) => String(t.course_id) === String(selectedCourseId))
      : (tests || []);
    base.forEach((t) => {
      const key = subjectKeyForTest(t);
      if (seen.has(key)) return;
      if (t.subject == null && t.subject_id == null) return;
      seen.set(key, { id: key, name: t.subject || "Unassigned" });
    });
    return sortHierarchyItems(Array.from(seen.values()));
  }, [tests, selectedCourseId, subjectKeyForTest]);

  const topicOptions = useMemo(() => {
    let base = tests;
    if (selectedCourseId) base = base.filter((t) => String(t.course_id) === String(selectedCourseId));
    if (selectedSubjectId) base = base.filter((t) => subjectKeyForTest(t) === selectedSubjectId);
    const seen = new Map(); // keyed by name to deduplicate display labels
    base.forEach((t) => {
      const topics = Array.isArray(t.master_topics) && t.master_topics.length > 0 ? t.master_topics : [];
      topics.forEach(({ name, sort_order }) => {
        if (name && !seen.has(name)) seen.set(name, { name, sort_order: sort_order ?? 0 });
      });
    });
    return Array.from(seen.values()).sort((a, b) => a.sort_order - b.sort_order || a.name.localeCompare(b.name));
  }, [tests, selectedCourseId, selectedSubjectId, subjectKeyForTest]);

  const weekOptions = useMemo(() => {
    if (allWeeks.length > 0) return allWeeks;
    const seen = new Map();
    (tests || []).forEach((t) => {
      const testWeeks = Array.isArray(t.weeks) && t.weeks.length > 0
        ? t.weeks
        : (t.week_id != null && t.week_id !== "" ? [{ id: t.week_id, number: t.week_number, label: t.week_label }] : []);
      testWeeks.forEach((w) => {
        const wid = String(w.id);
        if (seen.has(wid)) return;
        seen.set(wid, {
          id: wid,
          label: w.label || formatWeekOptionFallback(w.number),
          sortKey: Number(String(w.number ?? "").replace(/\D/g, "")) || 0,
        });
      });
    });
    return Array.from(seen.values()).sort((a, b) => a.sortKey - b.sortKey);
  }, [allWeeks, tests]);

  const filteredTests = useMemo(() => {
    let list = tests;
    if (showCourseFilter && selectedCourseId) list = list.filter((t) => String(t.course_id) === String(selectedCourseId));
    if (selectedSubjectId) {
      list = list.filter((t) => subjectKeyForTest(t) === selectedSubjectId);
    }
    if (selectedTopics.length > 0) {
      list = list.filter((t) => {
        const names = Array.isArray(t.master_topics) ? t.master_topics.map((mt) => mt.name) : [];
        return selectedTopics.some((sel) => names.includes(sel));
      });
    }
    if (showWeekFilter && selectedWeekId) {
      list = list.filter((t) => {
        if (Array.isArray(t.weeks) && t.weeks.length > 0) {
          return t.weeks.some((w) => String(w.id) === String(selectedWeekId));
        }
        return String(t.week_id) === String(selectedWeekId);
      });
    }
    if (selectedDifficulties.length > 0) {
      list = list.filter((t) => normalizeDifficulty(t.difficulty) && selectedDifficulties.includes(normalizeDifficulty(t.difficulty)));
    }
    if (cardSearch) list = list.filter((t) => t.name?.toLowerCase().includes(cardSearch.toLowerCase()));
    return sortHierarchyItems(list);
  }, [tests, selectedCourseId, selectedSubjectId, selectedTopics, selectedWeekId, selectedDifficulties, cardSearch, subjectKeyForTest, showWeekFilter, showCourseFilter]);

  const paginatedFilteredTests = useMemo(() => {
    const start = worksheetPage * worksheetRowsPerPage;
    return filteredTests.slice(start, start + worksheetRowsPerPage);
  }, [filteredTests, worksheetPage, worksheetRowsPerPage]);

  useEffect(() => { setWorksheetPage(0); }, [selectedCourseId, selectedSubjectId, selectedTopics, selectedWeekId, selectedDifficulties, cardSearch, tests]);

  const isWorksheetActive = useCallback((test) => {
    if (!test) return false;
    const raw = test.status ?? test.status_id ?? test.state ?? test.test_status;

    // `/student/ai-tests` currently does not include status in payload.
    // Treat missing/unknown as active for backward compatibility.
    if (raw === undefined || raw === null || raw === '') return true;

    // Numeric statuses (DB): 2=APPROVED (active), 3=REJECTED (inactive)
    const n = Number(raw);
    if (!Number.isNaN(n)) return n === 2;

    // String statuses (if introduced later)
    const s = String(raw).toLowerCase();
    if (['approved', 'active', 'enabled', 'publish', 'published'].includes(s)) return true;
    if (['rejected', 'inactive', 'disabled', 'deactivate', 'deactivated', 'unpublished'].includes(s)) return false;

    return true;
  }, []);

  useEffect(() => { setSelectedSubjectId(""); setSelectedTopics([]); }, [selectedCourseId]);
  useEffect(() => { setSelectedTopics([]); }, [selectedSubjectId]);

  const uniqueResultsTests = useMemo(() => {
    const seen = new Map();
    (allAttempts || []).forEach((a) => {
      if (a.test_id && !seen.has(a.test_id)) {
        seen.set(a.test_id, { id: a.test_id, name: a.test_name || `Test #${a.test_id}` });
      }
    });
    return sortHierarchyItems(Array.from(seen.values()));
  }, [allAttempts]);

  const filteredAllAttempts = useMemo(() => {
    let list = allAttempts;
    if (resultsTestFilter) list = list.filter((a) => String(a.test_id) === String(resultsTestFilter));
    if (resultsSearch) list = list.filter((a) => (a.test_name || "").toLowerCase().includes(resultsSearch.toLowerCase()));
    return list;
  }, [allAttempts, resultsTestFilter, resultsSearch]);

  // ─── fetch helpers ────────────────────────────────────────────────────────
  const fetchAllAttempts = useCallback(async (page = 1, includeTest = false) => {
    setLoadingAllAttempts(true);
    try {
      const params = { page, limit: 25 };
      if (isParent && selectedStudentId) params.student_id = selectedStudentId;
      if (includeTest) params.include_test = 1;
      const res = await api.get(`${aiTestUploadBasePath}/attempts`, { params });
      const payload = res?.data?.data;
      const list = Array.isArray(payload?.data) ? payload.data : [];
      setAllAttempts(list);
      setAttemptsPagination({ current_page: payload?.current_page ?? 1, last_page: payload?.last_page ?? 1, total: payload?.total ?? 0 });
    } catch (err) {
      console.error("Failed to load attempts", err);
      setAllAttempts([]);
      toast.error("Unable to load attempts");
    } finally {
      setLoadingAllAttempts(false);
    }
  }, [aiTestUploadBasePath, isParent, selectedStudentId]);

  const fetchStudents = useCallback(async () => {
    try {
      const response = await api.get("/parent/students", { params: { per_page: 100 } });
      const payload = response?.data?.data;
      const items = Array.isArray(payload?.data) ? payload.data : Array.isArray(payload) ? payload : [];
      setStudents(items.map((record) => {
        const student = record.student ?? {};
        const sid = record.child_id ?? student.id ?? record.id;
        const name = [student.first_name, student.last_name].filter(Boolean).join(" ") || record.display_name || "Student";
        return { id: sid, label: `${name}${student.email ? ` (${student.email})` : ""}` };
      }));
    } catch (error) {
      console.error("Failed to load students", error);
      toast.error("Unable to load students");
    }
  }, []);

  const fetchTests = useCallback(async (studentId) => {
    setFetchingTests(true);
    setFetchTestsError(false);
    try {
      const params = { limit: 500 };
      if (studentId) params.student_id = studentId;
      const response = await api.get(aiTestCatalogPath, { params });
      const payload = response?.data?.data;
      setTests(Array.isArray(payload?.ai_tests) ? payload.ai_tests : Array.isArray(payload) ? payload : []);
    } catch (error) {
      console.error("Failed to load AI tests", error);
      setTests([]);
      setFetchTestsError(true);
    } finally {
      setFetchingTests(false);
    }
  }, [aiTestCatalogPath]);

  const fetchAllWeeks = useCallback(async () => {
    try {
      const weeksPath = isParent ? "/parent/weeks" : "/student/fetch/weeks";
      const res = await api.get(weeksPath);
      const data = res?.data?.data;
      if (Array.isArray(data)) {
        setAllWeeks(data.map((w) => ({
          id: String(w.id),
          label: w.name || w.week_number || String(w.id),
          sortKey: Number(String(w.week_number ?? "").replace(/\D/g, "")) || 0,
        })));
      }
      const currentWeekId = res?.data?.current_week_id;
      if (currentWeekId != null && currentWeekId !== "") {
        setSelectedWeekId((prev) => prev || String(currentWeekId));
      }
    } catch {
      setAllWeeks([]);
    }
  }, [isParent]);

  const fetchAttemptHistory = useCallback(async (testId, studentId) => {
    if (!testId) return;
    setLoadingAttempts(true);
    try {
      const response = await api.get(`${aiTestUploadBasePath}/${testId}/results`, {
        params: studentId ? { student_id: studentId } : undefined,
      });
      const payload = response?.data?.data;
      setAttempts(Array.isArray(payload?.results) ? payload.results : []);
    } catch (error) {
      console.error("Failed to load previous attempts", error);
      setAttempts([]);
      toast.error("Unable to load previous attempts");
    } finally {
      setLoadingAttempts(false);
    }
  }, [aiTestUploadBasePath]);

  const fetchRetakeHistory = useCallback(async (testId, studentId) => {
    if (!testId) return;
    setLoadingRetakeHistory(true);
    try {
      const response = await api.get(`${aiTestUploadBasePath}/${testId}/retake-history`, {
        params: studentId ? { student_id: studentId } : undefined,
      });
      setRetakeHistory(response?.data?.data || null);
    } catch (error) {
      console.error("Failed to load retake history", error);
      setRetakeHistory(null);
    } finally {
      setLoadingRetakeHistory(false);
    }
  }, [aiTestUploadBasePath]);

  // ─── effects ─────────────────────────────────────────────────────────────

  // Check for pending monitoring session on mount
  useEffect(() => {
    try {
      const raw = localStorage.getItem(PENDING_UPLOAD_KEY);
      if (!raw) return;
      const session = JSON.parse(raw);
      if (session?.resultId && session?.testId && Date.now() - (session.savedAt || 0) < 24 * 3600 * 1000) {
        setPendingMonitorSession(session);
      } else {
        localStorage.removeItem(PENDING_UPLOAD_KEY);
      }
    } catch (_) {
      localStorage.removeItem(PENDING_UPLOAD_KEY);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (tab === TAB_RESULTS) {
      if (isParent && !isTesterAI && !selectedStudentId) { setAllAttempts([]); return; }
      fetchAllAttempts(attemptsPage, showPracticeResults);
    }
  }, [tab, attemptsPage, isParent, isTesterAI, selectedStudentId, showPracticeResults, fetchAllAttempts]);

  useEffect(() => {
    // Auto-enable practice results filter while in practice mode.
    if (isTestMode && !showPracticeResults) {
      setShowPracticeResults(true);
      setAttemptsPage(1);
    } else if (!isTestMode && showPracticeResults) {
      setShowPracticeResults(false);
      setAttemptsPage(1);
    }
  }, [isTestMode, showPracticeResults]);

  useEffect(() => {
    const urlTab = new URLSearchParams(location.search || "").get("tab");
    if (urlTab === "worksheets") {
      setTab(TAB_WORKSHEETS);
      return;
    }
    if (urlTab === "results") {
      setTab(TAB_RESULTS);
      return;
    }

    const state = location.state;
    if (state?.viewResult && state?.viewTestId && isValidTestId(state.viewTestId)) {
      if (isParent && state.studentId) setSelectedStudentId(state.studentId);
      setSelectedTestId(state.viewTestId);
      setResult(state.viewResult);
      setTab(TAB_WORKSHEETS);
      const sid = isParent ? (state.studentId || selectedStudentId) : undefined;
      fetchAttemptHistory(state.viewTestId, sid);
      fetchRetakeHistory(state.viewTestId, sid);
      window.history.replaceState({}, document.title, window.location.pathname);
    } else if (state?.viewTestId && !state?.viewResult && isValidTestId(state.viewTestId)) {
      // Navigating back from WorkSheetSweepHistory "Start Sweep".
      if (isParent && state.studentId) setSelectedStudentId(state.studentId);
      pendingAutoSweepRef.current = state.autoSweepAttemptId || '__open__';
      setSelectedTestId(state.viewTestId);
      setTab(TAB_RESULTS);
      window.history.replaceState({}, document.title, window.location.pathname);
    } else if (state?.openMyResults) {
      // Navigating back from WorkSheetReview — open My Results tab directly.
      setTab(TAB_RESULTS);
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }, [location.search, location.state, isParent, selectedStudentId, fetchAttemptHistory, fetchRetakeHistory]);

  useEffect(() => {
    // Testers on parent portal have no linked children — load the full worksheet catalog directly.
    if (isTesterAI && isParentPortal) {
      fetchTests();
      fetchAllWeeks();
      return;
    }
    if (isParent) fetchStudents(); else fetchTests();
    fetchAllWeeks();
  }, [isParent, isTesterAI, isParentPortal, fetchStudents, fetchTests, fetchAllWeeks]);

  useEffect(() => {
    if (!isParent) return;
    if (isTesterAI) return;
    if (selectedStudentId) fetchTests(selectedStudentId);
    else { setTests([]); setSelectedTestId(""); }
  }, [isParent, isTesterAI, selectedStudentId, fetchTests]);

  useEffect(() => {
    if (!selectedTestId || !isValidTestId(selectedTestId)) {
      setAttempts([]); setResult(null); setRetakeSession(null); setRetakeAnswers({});
      setRetakeResult(null); setRetakeHistory(null); setActiveSwapeMeta(null);
      setShowPreviousAttempts(false); setShowSweepTracker(false); setShowEvaluationSummary(false);
      return;
    }
    if (isParent && !isTesterAI && !selectedStudentId) return;
    fetchAttemptHistory(selectedTestId, isParent && !isTesterAI ? selectedStudentId : undefined);
    fetchRetakeHistory(selectedTestId, isParent && !isTesterAI ? selectedStudentId : undefined);
  }, [selectedTestId, selectedStudentId, isParent, isTesterAI, fetchAttemptHistory, fetchRetakeHistory]);

  useEffect(() => { if (result) setShowEvaluationSummary(true); }, [result]);

  // Auto-open Previous Attempts (and optionally trigger Sweep dialog) after
  // navigating back from WorkSheetSweepHistory with a pending auto-sweep.
  useEffect(() => {
    if (!pendingAutoSweepRef.current || attempts.length === 0) return;
    const pendingId = pendingAutoSweepRef.current;
    pendingAutoSweepRef.current = null;
    setShowPreviousAttempts(true);
    if (pendingId !== '__open__') {
      const target = attempts.find((a) => String(a.id) === String(pendingId));
      if (target) {
        setPendingSwapeAttempt(target);
        setSwapeConfirmOpen(true);
      }
    }
  }, [attempts]);

  // Sweep timer
  useEffect(() => {
    if (!retakeSession?.questions?.length) {
      setSwapeTotalSeconds(0); setSwapeTotalRemaining(0); setSwapeAutoSubmitted(false); setRetakePage(1);
      return undefined;
    }
    const totalSeconds = retakeSession.questions.reduce((sum, q) => {
      const raw = q?.time_limit_seconds ?? q?.time_limit ?? q?.time_limit_sec ?? q?.duration_in_sec ?? q?.question?.duration_in_sec ?? 0;
      return sum + parseTimeLimitSeconds(raw);
    }, 0);
    setSwapeTotalSeconds(totalSeconds); setSwapeTotalRemaining(totalSeconds); setSwapeAutoSubmitted(false);
    if (!totalSeconds) return undefined;
    const intervalId = setInterval(() => setSwapeTotalRemaining((prev) => (prev > 0 ? prev - 1 : 0)), 1000);
    return () => clearInterval(intervalId);
  }, [retakeSession?.token, retakeSession?.questions]);

  useEffect(() => {
    if (!retakeSession?.questions?.length) return;
    if (swapeTotalSeconds === 0 || swapeTotalRemaining > 0 || swapeAutoSubmitted || submittingRetake) return;
    setSwapeAutoSubmitted(true);
    handleSubmitRetake();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [swapeTotalRemaining, swapeTotalSeconds, retakeSession?.token, swapeAutoSubmitted, submittingRetake]);

  const paginatedRetakeQuestions = useMemo(() => {
    if (!retakeSession?.questions?.length) return { items: [], totalPages: 1 };
    const totalPages = Math.max(1, Math.ceil(retakeSession.questions.length / RETAKE_PAGE_SIZE));
    const safePage = Math.min(Math.max(retakePage, 1), totalPages);
    const startIndex = (safePage - 1) * RETAKE_PAGE_SIZE;
    return { items: retakeSession.questions.slice(startIndex, startIndex + RETAKE_PAGE_SIZE), totalPages };
  }, [retakeSession?.questions, retakePage, RETAKE_PAGE_SIZE]);

  const paginatedEvaluationAnswers = useMemo(() => {
    if (!result?.answers?.length) return { items: [], totalPages: 1 };
    const totalPages = Math.max(1, Math.ceil(result.answers.length / EVALUATION_PAGE_SIZE));
    const safePage = Math.min(Math.max(evaluationPage, 1), totalPages);
    const startIndex = (safePage - 1) * EVALUATION_PAGE_SIZE;
    return {
      items: result.answers.slice(startIndex, startIndex + EVALUATION_PAGE_SIZE).map((a, idx) => ({ ...a, displayNumber: startIndex + idx + 1 })),
      totalPages,
    };
  }, [result?.answers, evaluationPage, EVALUATION_PAGE_SIZE]);

  // ─── handlers ────────────────────────────────────────────────────────────
  const dragDepthRef = useRef(0);
  const [dropZoneActive, setDropZoneActive] = useState(false);

  const isAcceptedAnswerSheetFile = (f) => {
    if (!f) return false;
    const okMime = ["application/pdf", "image/png", "image/jpeg"].includes(f.type);
    if (okMime) return true;
    return /\.(pdf|png|jpe?g)$/i.test(f.name || "");
  };

  const applyAnswerSheetFile = (f) => {
    if (!f) return;
    if (!isAcceptedAnswerSheetFile(f)) {
      toast.error("Please upload a PDF, PNG, or JPG file.");
      return;
    }
    setFile(f);
  };

  const handleFileChange = (event) => {
    const s = event.target.files?.[0];
    if (s) applyAnswerSheetFile(s);
    event.target.value = "";
  };

  const handleDropZoneDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    dragDepthRef.current += 1;
    setDropZoneActive(true);
  };

  const handleDropZoneDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    dragDepthRef.current -= 1;
    if (dragDepthRef.current <= 0) {
      dragDepthRef.current = 0;
      setDropZoneActive(false);
    }
  };

  const handleDropZoneDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    try {
      e.dataTransfer.dropEffect = "copy";
    } catch (_) {}
  };

  const handleDropZoneDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    dragDepthRef.current = 0;
    setDropZoneActive(false);
    const f = e.dataTransfer.files?.[0];
    if (f) applyAnswerSheetFile(f);
  };

  const pollExplanationStatus = async (resultId, testId) => {
    if (pollingPausedRef.current) return;
    try {
      const response = await api.get(`${aiTestUploadBasePath}/${testId || selectedTestId}/processing-status/${resultId}`);
      const data = response?.data?.data;
      if (data?.explanation_status === "completed") {
        setProcessingStage("complete");
        setPollingResultId(null);
        localStorage.removeItem(PENDING_UPLOAD_KEY);
        if (data.answers) setResult((prev) => (prev ? { ...prev, answers: data.answers } : prev));
        await fetchAttemptHistory(testId || selectedTestId, isParent ? selectedStudentId : undefined);
        toast.success("Explanations generated successfully");
      } else if (data?.explanation_status === "failed") {
        setProcessingStage("complete");
        setPollingResultId(null);
        localStorage.removeItem(PENDING_UPLOAD_KEY);
        toast.warning("Explanation generation failed. Results are still available.");
      } else {
        if (!pollingPausedRef.current) {
          setTimeout(() => pollExplanationStatus(resultId, testId), 3000);
        }
      }
    } catch (error) {
      if (!pollingPausedRef.current) {
        setTimeout(() => pollExplanationStatus(resultId, testId), 5000);
      }
    }
  };

  // Poll for AI semantic grading completion (fill-in-the-blank / short answer re-grading)
  const pollAiGradingStatus = async (resultId, testId) => {
    const MAX_POLLS = 10; // ~30s at 3s intervals
    try {
      const response = await api.get(`${aiTestUploadBasePath}/${testId}/processing-status/${resultId}`);
      const data = response?.data?.data;
      const status = data?.ai_grading_status;

      if (status === "completed") {
        setAiGradingStatus("completed");
        aiGradingPollCountRef.current = 0;
        if (data.answers || data.summary) {
          setResult((prev) => {
            if (!prev) return prev;
            return {
              ...prev,
              ...(data.answers ? { answers: data.answers } : {}),
              ...(data.summary ? { summary: data.summary } : {}),
              ai_grading_status: "completed",
            };
          });
        }
        toast.success("AI grading complete — scores updated.");
        await fetchAttemptHistory(testId, isParent ? selectedStudentId : undefined);
        return;
      }

      if (status === "failed") {
        setAiGradingStatus("failed");
        aiGradingPollCountRef.current = 0;
        return;
      }

      aiGradingPollCountRef.current += 1;
      if (aiGradingPollCountRef.current >= MAX_POLLS) {
        setAiGradingStatus("timeout");
        aiGradingPollCountRef.current = 0;
        return;
      }

      aiGradingPollRef.current = setTimeout(() => pollAiGradingStatus(resultId, testId), 3000);
    } catch {
      aiGradingPollCountRef.current += 1;
      if (aiGradingPollCountRef.current >= MAX_POLLS) {
        setAiGradingStatus("timeout");
        aiGradingPollCountRef.current = 0;
        return;
      }
      aiGradingPollRef.current = setTimeout(() => pollAiGradingStatus(resultId, testId), 5000);
    }
  };

  // Cleanup AI grading poll on unmount
  useEffect(() => () => { if (aiGradingPollRef.current) clearTimeout(aiGradingPollRef.current); }, []);

  // Validate the file against the selected worksheet before starting analysis.
  // Returns true if the caller should proceed, false if they should abort.
  const validateBeforeUpload = async (navigateToReview = false) => {
    if (!selectedTestId || !file) return false;
    const form = new FormData();
    form.append("answer_sheet", file);
    if (isParent && selectedStudentId) form.append("student_id", selectedStudentId);
    setValidating(true);
    try {
      const res = await api.post(`${aiTestUploadBasePath}/${selectedTestId}/validate-sheet`, form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const data = res?.data?.data;
      const confidence = data?.confidence ?? "skipped";
      const method = data?.method ?? "unknown";
      // Always proceed if validation was skipped (QR/Gemini unavailable)
      if (!data || confidence === "skipped" || method === "skipped") return true;
      // Valid result
      if (data.valid) {
        if (data.issues?.length > 0) {
          // Non-critical warning (e.g. question count mismatch) — show info dialog
          setValidationWarning({ issues: data.issues, testName: data.test_name || "", method, navigateToReview, warnOnly: true });
          return false;
        }
        // Clean match — flash green confirmation then proceed
        if (method === "qr" || method === "ocr_text") {
          setValidationOk(true);
          await new Promise((r) => setTimeout(r, 900));
          setValidationOk(false);
        }
        return true;
      }
      // Invalid — show warning dialog
      setValidationWarning({ issues: data.issues || [], testName: data.test_name || "", method, navigateToReview, warnOnly: false });
      return false;
    } catch (_) {
      // Network/server error — don't block the student
      return true;
    } finally {
      setValidating(false);
    }
  };

  const handleUpload = async (options = {}) => {
    const { navigateToReview = false } = options;
    if (!selectedTestId || !file || (isParent && !selectedStudentId)) {
      toast.warning("Select a student, AI test, and upload a file first.");
      return null;
    }
    const form = new FormData();
    form.append("answer_sheet", file);
    if (isParent) form.append("student_id", selectedStudentId);
    setUploadProgress(0); setProcessingStage("uploading");
    if (aiGradingPollRef.current) { clearTimeout(aiGradingPollRef.current); aiGradingPollRef.current = null; }
    setAiGradingStatus(null);
    try {
      setUploading(true); setResult(null);
      const response = await api.post(`${aiTestUploadBasePath}/${selectedTestId}/answer-sheet`, form, {
        headers: { "Content-Type": "multipart/form-data" },
        onUploadProgress: (e) => {
          const percent = Math.round((e.loaded * 100) / e.total);
          setUploadProgress(percent);
          if (percent === 100) setProcessingStage("processing");
        },
      });
      const resultData = response?.data?.data || null;
      setResult(resultData);
      await fetchAttemptHistory(selectedTestId, isParent ? selectedStudentId : undefined);
      // Start AI grading poll if there are uncertain answers being re-graded
      if (resultData?.ai_grading_status === "pending" && resultData?.id) {
        setAiGradingStatus("pending");
        aiGradingPollCountRef.current = 0;
        aiGradingPollRef.current = setTimeout(() => pollAiGradingStatus(resultData.id, selectedTestId), 3000);
      } else {
        setAiGradingStatus(resultData?.ai_grading_status || null);
      }

      if (resultData?.explanation_status === "pending" && resultData?.id && !navigateToReview) {
        setProcessingStage("explanations");
        setPollingResultId(resultData.id);
        pollingPausedRef.current = false;
        setPollingPaused(false);
        pollExplanationStatus(resultData.id, selectedTestId);
        toast.success("Answer sheet processed. Generating explanations...");
      } else {
        setProcessingStage("complete");
        toast.success(
          navigateToReview
            ? "Answer sheet processed. Opening review…"
            : (response?.data?.message || "Answer sheet processed"),
        );
      }
      return resultData;
    } catch (error) {
      const errMsg = error?.response?.data?.message || error?.response?.data?.error || "Upload failed";
      const isTestMismatch = /different.*test|wrong.*sheet|does not match|belongs to/i.test(errMsg);
      if (isTestMismatch) {
        const testObj = tests.find((t) => String(t.id) === String(selectedTestId));
        setValidationWarning({
          issues: [errMsg],
          testName: testObj?.name || `Test #${selectedTestId}`,
          method: "upload_error",
          navigateToReview: false,
          warnOnly: false,
        });
      } else {
        toast.error(errMsg);
      }
      setProcessingStage(null);
      return null;
    } finally {
      setUploading(false);
    }
  };

  const disableSubmit = useMemo(() => {
    if (!selectedTestId || !file) return true;
    if (isParent && !selectedStudentId) return true;
    return uploading || validating;
  }, [selectedTestId, file, isParent, selectedStudentId, uploading, validating]);

  const totalQuestions = useMemo(() => result?.summary?.total_questions ?? result?.summary?.total ?? result?.summary?.totalQuestions ?? 0, [result]);
  const gradableTotal = useMemo(() => result?.summary?.gradable_total ?? totalQuestions, [result, totalQuestions]);
  const correctAnswers = useMemo(() => result?.summary?.correct_answers ?? result?.summary?.correctAnswers ?? 0, [result]);
  const incorrectAnswers = useMemo(() => Math.max(gradableTotal - correctAnswers, 0), [gradableTotal, correctAnswers]);
  const summaryPercentage = useMemo(() => {
    if (!result?.summary) return 0;
    const s = result.summary;
    if (typeof s.percentage === "number") return s.percentage;
    if (typeof s.percent === "number") return s.percent;
    return gradableTotal ? Number(((correctAnswers / gradableTotal) * 100).toFixed(2)) : 0;
  }, [result, gradableTotal, correctAnswers]);

  const getAttemptStats = (attempt) => {
    const summary = attempt?.summary ?? {};
    const answers = Array.isArray(attempt?.answers) ? attempt.answers : [];
    const rawTotal = summary.total_questions ?? summary.total ?? summary.totalQuestions ?? summary.totalQuestionsCount;
    const totalQuestionsValue = typeof rawTotal === "number" ? rawTotal : answers.length;
    const gradableTotal = summary.gradable_total ?? totalQuestionsValue;
    const writingCount = summary.writing_count ?? 0;
    const rawCorrect = summary.correct_answers ?? summary.correctAnswers;
    const correctAnswersValue = typeof rawCorrect === "number" ? rawCorrect : answers.filter((a) => a?.is_correct).length;
    const safeGradable = gradableTotal || answers.length;
    const incorrectAnswersValue = Math.max((safeGradable || 0) - correctAnswersValue, 0);
    const rawPercentage = summary.percentage ?? summary.percent;
    const computedPercentage = safeGradable ? Number(((correctAnswersValue / safeGradable) * 100).toFixed(2)) : 0;
    return { totalQuestions: totalQuestionsValue, gradableTotal: safeGradable, writingCount, correctAnswers: correctAnswersValue, incorrectAnswers: incorrectAnswersValue, percentage: typeof rawPercentage === "number" ? rawPercentage : computedPercentage };
  };

  const formatAttemptTimestamp = (value) => {
    if (!value) return "—";
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? "—" : date.toLocaleString();
  };

  const formatAttemptDate = (iso) => {
    if (!iso) return "N/A";
    try {
      const d = new Date(iso);
      return d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric", hour: "2-digit", minute: "2-digit" });
    } catch { return iso; }
  };

  const handleRetakeIncorrect = async (attempt) => {
    if (!attempt) return;
    if (isParent && !selectedStudentId) { toast.warning("Select a student before starting Sweep."); return; }
    const testId = attempt?.test_id || selectedTestId;
    if (!testId) { toast.error("Choose a test before starting Sweep."); return; }
    // question_ids omitted — backend computes all eligible (answered-wrong + unanswered)
    const payload = { result_attempt_count: attempt.attempt_count };
    if (isParent && selectedStudentId) payload.student_id = selectedStudentId;
    try {
      setRetakeLoading((prev) => ({ ...prev, [attempt.id]: true }));
      setRetakeResult(null);
      const response = await api.post(`${aiTestUploadBasePath}/${testId}/retake`, payload);
      const data = response?.data?.data;
      if (!data?.questions?.length) { toast.info("No questions available for Sweep."); return; }
      const answerNumberMap = Array.isArray(attempt?.answers)
        ? attempt.answers.reduce((acc, a) => { if (a?.question_id) acc[a.question_id] = a.question_number ?? a.question_no ?? a.sequence; return acc; }, {})
        : {};
      const flaggedQuestionIds = data.questions.filter((q) => q.is_flagged_by_user).map((q) => q.question_id);
      setFlaggedQuestions(new Set(flaggedQuestionIds));
      setRetakeSession({
        token: data.retake_token, testId: data.test_id, studentId: data.student_id,
        questions: data.questions.map((q, index) => ({ ...q, displayNumber: answerNumberMap[q.question_id] ?? q.sequence ?? index + 1 })),
      });
      setActiveSwapeMeta({
        swapeSessionsUsed: data.swape_sessions_used, swapeSessionsRemaining: data.swape_sessions_remaining,
        maxSwapesPerUpload: data.max_swapes_per_upload, resultAttemptCount: data.result_attempt_count ?? attempt.attempt_count,
      });
      setRetakeAnswers(data.questions.reduce((acc, q) => { acc[q.question_id] = ""; return acc; }, {}));
      toast.info(`${data.questions.length} incorrect questions ready for Sweep.`);
    } catch (error) {
      toast.error(error?.response?.data?.message || error?.response?.data?.error || "Unable to start Sweep");
    } finally {
      setRetakeLoading((prev) => { const next = { ...prev }; delete next[attempt.id]; return next; });
    }
  };

  const handleSwapeClick = (attempt) => { setPendingSwapeAttempt(attempt); setSwapeConfirmOpen(true); };
  const handleConfirmSwape = () => { setSwapeConfirmOpen(false); if (pendingSwapeAttempt) handleRetakeIncorrect(pendingSwapeAttempt); setPendingSwapeAttempt(null); };
  const handleCancelSwape = () => { setSwapeConfirmOpen(false); setPendingSwapeAttempt(null); };

  const handleRetakeAnswerChange = (questionId, letter) => {
    if (swapeTotalSeconds > 0 && swapeTotalRemaining === 0) { toast.warning("Sweep time is up."); return; }
    setRetakeAnswers((prev) => ({ ...prev, [questionId]: letter }));
  };

  const handleFlagQuestion = async (questionId) => {
    if (!retakeSession?.testId) { toast.error("No active Sweep session."); return; }
    const isFlagged = flaggedQuestions.has(questionId);
    try {
      setFlaggingQuestion((prev) => ({ ...prev, [questionId]: true }));
      const payload = {};
      if (isParent && selectedStudentId) payload.student_id = selectedStudentId;
      let response;
      if (isFlagged) {
        response = await api.delete(`${aiTestUploadBasePath}/${retakeSession.testId}/questions/${questionId}/flag`, { data: payload });
        setFlaggedQuestions((prev) => { const next = new Set(prev); next.delete(questionId); return next; });
      } else {
        response = await api.post(`${aiTestUploadBasePath}/${retakeSession.testId}/questions/${questionId}/flag`, payload);
        setFlaggedQuestions((prev) => new Set([...prev, questionId]));
      }
      toast.success(response?.data?.message || (isFlagged ? "Question unflagged." : "Question flagged."));
    } catch (error) {
      toast.error(error?.response?.data?.message || `Unable to ${isFlagged ? "unflag" : "flag"} question`);
    } finally {
      setFlaggingQuestion((prev) => { const next = { ...prev }; delete next[questionId]; return next; });
    }
  };

  const handleSubmitRetake = async () => {
    if (!retakeSession?.questions?.length) { toast.warning("No Sweep questions loaded."); return; }
    const answers = retakeSession.questions.map((q) => {
      const sel = retakeAnswers[q.question_id];
      if (!sel) return null;
      return { question_id: q.question_id, selected_letter: sel };
    }).filter(Boolean);
    if (!answers.length) { toast.warning("Select answers before submitting Sweep."); return; }
    const payload = { retake_token: retakeSession.token, answers, result_attempt_count: activeSwapeMeta?.resultAttemptCount };
    if (isParent && selectedStudentId) payload.student_id = selectedStudentId;
    try {
      setSubmittingRetake(true);
      const response = await api.post(`${aiTestUploadBasePath}/${retakeSession.testId}/retake/submit`, payload);
      const data = response?.data?.data;
      setRetakeResult(data || null);
      await fetchRetakeHistory(retakeSession.testId, isParent ? selectedStudentId : undefined);
      toast.success(response?.data?.message || "Sweep submitted.");
    } catch (error) {
      toast.error(error?.response?.data?.message || "Unable to submit Sweep");
    } finally {
      setSubmittingRetake(false);
    }
  };

  const handleClearRetake = () => {
    setRetakeSession(null); setRetakeAnswers({}); setRetakeResult(null); setActiveSwapeMeta(null);
    setSwapeTotalSeconds(0); setSwapeTotalRemaining(0); setSwapeAutoSubmitted(false); setRetakePage(1);
  };

  const buildIncorrectOnlyResult = (attempt) => attempt ? { ...attempt, answers: Array.isArray(attempt?.answers) ? attempt.answers.filter((a) => !a?.is_correct) : [] } : attempt;

  const fetchDetailedResult = async (attempt, filterIncorrect = false) => {
    try {
      const response = await api.get(`${aiTestUploadBasePath}/${selectedTestId}/results`, {
        params: { student_id: isParent ? selectedStudentId : undefined, include_questions: 1 },
      });
      const data = response?.data?.data || {};
      const allResults = data?.results || [];
      const testMeta = data?.test || {};
      const targetResult = allResults.find((r) => r.id === attempt.id);
      if (targetResult) {
        setResultTestMeta({ subject: testMeta.subject, strand: testMeta.strand, subtopic: testMeta.subtopic, test_name: testMeta.test_name });
        setResult(filterIncorrect ? buildIncorrectOnlyResult(targetResult) : targetResult);
        setEvaluationPage(1); setHideCorrectAnswers(filterIncorrect ? false : !isParent);
      }
    } catch (error) {
      toast.error("Unable to load detailed question information");
      setResult(filterIncorrect ? buildIncorrectOnlyResult(attempt) : attempt);
      setEvaluationPage(1); setHideCorrectAnswers(filterIncorrect ? false : !isParent);
    }
  };

  const formatSeconds = formatTime;

  const makeAbsoluteUrl = (url) => {
    if (!url || typeof url !== "string") return url;
    const trimmed = url.trim();
    const r2Match = trimmed.match(/^https?:\/\/pub-[^/]+\.r2\.dev\/(.+)$/);
    if (r2Match) return `${(api.defaults.baseURL || "").replace(/\/$/, "")}/proxy/visual/${r2Match[1]}`;
    if (trimmed.startsWith("http://") || trimmed.startsWith("https://")) return trimmed;
    if (trimmed.startsWith("//")) return `https:${trimmed}`;
    const baseURL = api.defaults.baseURL || "";
    return baseURL ? `${baseURL.replace(/\/$/, "")}${trimmed.startsWith("/") ? "" : "/"}${trimmed}` : trimmed;
  };

  const resolveVisualPayload = (value) => {
    if (!value) return null;
    if (typeof value === "string") return makeAbsoluteUrl(value);
    if (Array.isArray(value)) return value.length > 0 ? resolveVisualPayload(value[0]) : null;
    if (typeof value === "object") {
      const urlCandidate = [value.url, value.r2_url, value.public_url, value.image_url, value.src, value.path].find((u) => typeof u === "string" && u.trim().length > 0);
      return urlCandidate ? makeAbsoluteUrl(urlCandidate) : null;
    }
    return null;
  };

  const resolveQuestionVisual = (source) => {
    const inlineSvg = source?.visual_inline_svg || source?.question?.visual_inline_svg || null;
    const inlineImageRaw = source?.visual_inline_image || source?.question?.visual_inline_image || source?.visual?.url || source?.question?.visual?.url || null;
    const inlineImage = resolveVisualPayload(inlineImageRaw);
    const directUrl = source?.visual_image_url || source?.image_url || source?.visual_url || source?.question?.visual_image_url || source?.question?.visual_url || source?.question?.image_url || source?.question_image || source?.question?.question_image || source?.image || source?.question?.image || null;
    const visualUrlRaw = directUrl || source?.visual?.url || source?.visual?.r2_url || source?.visual?.public_url || source?.visual?.image_url || source?.visual?.src || source?.question?.visual?.url || source?.question?.visual?.r2_url || source?.question?.visual?.public_url || source?.question?.visual?.image_url || source?.question?.visual?.src || source?.visual || source?.question?.visual || null;
    const visualUrl = resolveVisualPayload(visualUrlRaw);
    const alt = source?.visual_alt || source?.question?.visual_alt || source?.visual?.alt || source?.question?.visual?.alt || null;
    return { inlineSvg, resolvedVisualUrl: inlineImage || visualUrl, alt };
  };

  const handleViewSwapeSession = (session) => { setSelectedSwapeSession(session); setSwapeSessionModalOpen(true); };
  const handleCloseSwapeModal = () => { setSwapeSessionModalOpen(false); setSelectedSwapeSession(null); };
  const handleOpenActionMenu = (event, attemptId) => setActionMenuAnchor((prev) => ({ ...prev, [attemptId]: event.currentTarget }));
  const handleCloseActionMenu = (attemptId) => setActionMenuAnchor((prev) => { const next = { ...prev }; delete next[attemptId]; return next; });
  const handleDoOnline = (test) => {
    if (!isWorksheetActive(test)) {
      toast.error("This work sheet is currently inactive.");
      return;
    }
    if (!isValidTestId(test?.id)) {
      toast.error("Invalid work sheet. Please refresh the page.");
      return;
    }
    navigate(`/${prefix}/work-sheet/${test.id}/take`, {
      state: {
        ...(isParent && selectedStudentId ? { studentId: selectedStudentId } : {}),
        isTestMode: isTestMode,
      },
    });
  };

  const handleNavigateReview = (testId, attemptId) => {
    if (!isValidTestId(testId) || !attemptId) return;
    navigate(`/${prefix}/work-sheet/${testId}/attempt/${attemptId}/review`, {
      state: isParent && selectedStudentId ? { studentId: selectedStudentId } : undefined,
    });
  };

  const handleDownloadVariant = async (test, variant) => {
    if (!isWorksheetActive(test)) {
      toast.error("This work sheet is currently inactive.");
      return;
    }
    if (variant === "with-answers") {
      if (!canDownloadAnswerKey) {
        toast.error("You do not have permission to download answer keys.");
        return;
      }
      if (!allowAnswerKeyDownload) {
        toast.error("Answer key downloads are disabled by your administrator.");
        return;
      }
    }
    const key = `${test.id}-${variant}`;
    if (downloadingKey[key]) return;
    setDownloadMenuAnchor((prev) => { const n = { ...prev }; delete n[test.id]; return n; });
    setDownloadingKey((prev) => ({ ...prev, [key]: true }));
    const labelMap = { "question-paper": "Question Paper", "answer-sheet": "Answer Sheet", "with-answers": "Answer key" };
    try {
      const response = await api.get(`${aiTestCatalogPath}/${test.id}/download-pdf?variant=${variant}`, { responseType: "blob" });
      const contentType = response.headers?.["content-type"] || "";
      if (!contentType.includes("application/pdf")) {
        const text = await response.data.text?.();
        let msg = "Download failed";
        try { msg = JSON.parse(text)?.message || msg; } catch (_) {}
        toast.error(msg); return;
      }
      const url = URL.createObjectURL(new Blob([response.data], { type: "application/pdf" }));
      const link = document.createElement("a");
      link.href = url; link.download = `${test.name || "work-sheet"}-${variant}.pdf`;
      document.body.appendChild(link); link.click(); document.body.removeChild(link); URL.revokeObjectURL(url);
      toast.success(`${labelMap[variant] || "File"} downloaded.`);
    } catch (err) {
      toast.error(err?.response?.data?.message || "Unable to download");
    } finally {
      setDownloadingKey((prev) => { const n = { ...prev }; delete n[key]; return n; });
    }
  };

  const handleOpenUpload = (test) => {
    if (!isWorksheetActive(test)) {
      toast.error("This work sheet is currently inactive.");
      return;
    }
    if (!isValidTestId(test?.id)) {
      toast.error("Invalid work sheet. Please refresh the page.");
      return;
    }
    setSelectedTestId(test.id);
    setUploadDialogOpen(true);
    setFile(null);
    setResult(null);
  };
  const handleCloseUploadDialog = () => {
    setUploadDialogOpen(false);
    setFile(null);
    if (processingStage !== "explanations") {
      setProcessingStage(null);
    }
    dragDepthRef.current = 0;
    setDropZoneActive(false);
  };

  const handlePausePolling = useCallback(() => {
    if (!pollingResultId || pollingPausedRef.current) return;
    pollingPausedRef.current = true;
    setPollingPaused(true);
    try {
      const session = {
        resultId: pollingResultId,
        testId: selectedTestId,
        studentId: selectedStudentId || null,
        savedAt: Date.now(),
      };
      localStorage.setItem(PENDING_UPLOAD_KEY, JSON.stringify(session));
    } catch (_) {}
    setPendingMonitorSession({ resultId: pollingResultId, testId: selectedTestId, studentId: selectedStudentId || null });
    toast.info("Monitoring paused. You can continue later.");
  }, [pollingResultId, selectedTestId, selectedStudentId]);

  const handleResumePolling = useCallback(() => {
    if (!pollingResultId || !pollingPausedRef.current) return;
    pollingPausedRef.current = false;
    setPollingPaused(false);
    pollExplanationStatus(pollingResultId, selectedTestId);
    toast.success("Monitoring resumed.");
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pollingResultId, selectedTestId]);

  const handleContinueMonitoring = useCallback(() => {
    try {
      const raw = localStorage.getItem(PENDING_UPLOAD_KEY);
      if (!raw) return;
      const session = JSON.parse(raw);
      if (!session?.resultId || !session?.testId) return;
      setSelectedTestId(session.testId);
      if (session.studentId) setSelectedStudentId(session.studentId);
      setPollingResultId(session.resultId);
      pollingPausedRef.current = false;
      setPollingPaused(false);
      setProcessingStage("explanations");
      setPendingMonitorSession(null);
      setUploadDialogOpen(true);
      pollExplanationStatus(session.resultId, session.testId);
      toast.success("Monitoring resumed — watching for explanations.");
    } catch (_) {
      localStorage.removeItem(PENDING_UPLOAD_KEY);
      setPendingMonitorSession(null);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleDismissMonitorSession = useCallback(() => {
    localStorage.removeItem(PENDING_UPLOAD_KEY);
    setPendingMonitorSession(null);
  }, []);

  const weakAreasBySubject = useMemo(() => {
    if (!isParent || allAttempts.length === 0) return [];
    const byKey = {};
    allAttempts.forEach((a) => {
      const key = [a.subject || "Unknown", a.strand || "", a.subtopic || ""].filter(Boolean).join(" › ");
      if (!byKey[key]) byKey[key] = { key, subject: a.subject, strand: a.strand, subtopic: a.subtopic, total: 0, correct: 0, attempts: 0 };
      byKey[key].total += a.total_questions || 0; byKey[key].correct += a.correct_answers || 0; byKey[key].attempts += 1;
    });
    return Object.values(byKey).map((r) => ({ ...r, percentage: r.total > 0 ? Math.round((r.correct / r.total) * 1000) / 10 : 0 })).sort((a, b) => a.percentage - b.percentage);
  }, [isParent, allAttempts]);

  // ─── render helpers ───────────────────────────────────────────────────────
  const renderQuestionVisual = (source, keyPrefix = "") => {
    const { inlineSvg, resolvedVisualUrl, alt } = resolveQuestionVisual(source);
    if (inlineSvg) {
      return (
        <Box className="question-visual question-visual--inline" aria-label={alt || "Question visual"}
          sx={{ width: "100%", maxHeight: 320, overflow: "auto", borderRadius: 2, border: "1px solid #e0e0e0", mb: 2, p: 1 }}
          dangerouslySetInnerHTML={{ __html: inlineSvg }} />
      );
    }
    if (!resolvedVisualUrl) return null;
    const imgKey = `${keyPrefix}-${source?.question_id || source?.id || "img"}`;
    return (
      <Box sx={{ mb: 2, position: "relative" }}>
        {imageLoading.has(imgKey) && !imageErrors.has(imgKey) && (
          <Box sx={{ width: "100%", minHeight: 200, display: "flex", alignItems: "center", justifyContent: "center", borderRadius: 2, border: "1px solid #e0e0e0", background: "#f5f5f5" }}>
            <CircularProgress size={40} />
          </Box>
        )}
        {!imageErrors.has(imgKey) && (
          <Box key={`img-${imgKey}-${imageRetries.get(imgKey) || 0}`} component="img" src={resolvedVisualUrl} alt={alt || "Question visual"}
            onLoad={() => setImageLoading((prev) => { const n = new Set(prev); n.delete(imgKey); return n; })}
            onError={() => {
              setImageLoading((prev) => { const n = new Set(prev); n.delete(imgKey); return n; });
              const retries = imageRetries.get(imgKey) || 0;
              if (retries < 1) setImageRetries((prev) => new Map(prev).set(imgKey, retries + 1));
              else setImageErrors((prev) => new Set([...prev, imgKey]));
            }}
            onLoadStart={() => setImageLoading((prev) => new Set([...prev, imgKey]))}
            sx={{ width: "100%", maxHeight: 320, objectFit: "contain", borderRadius: 2, border: "1px solid #e0e0e0", display: imageLoading.has(imgKey) ? "none" : "block" }} />
        )}
        {imageErrors.has(imgKey) && (
          <Box sx={{ width: "100%", minHeight: 160, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", borderRadius: 2, border: "1px solid #e0e0e0", background: "#f5f5f5", p: 2 }}>
            <Typography variant="body2" color="text.secondary" sx={{ textAlign: "center" }}>{alt || "Question visual"}</Typography>
            <Typography variant="caption" color="text.secondary" sx={{ mt: 1, fontStyle: "italic" }}>Image could not be loaded</Typography>
          </Box>
        )}
      </Box>
    );
  };

  // ─── JSX ──────────────────────────────────────────────────────────────────
  const activeFilterCount = [cardSearch, showCourseFilter ? selectedCourseId : "", selectedSubjectId, selectedTopics.length > 0 ? "t" : "", showWeekFilter ? selectedWeekId : "", selectedDifficulties.length > 0 ? "d" : ""].filter(Boolean).length;
  const worksheetTableColumnCount = showCourseFilter ? 7 : 6; // Name, [Course], Subject, Topic, Subtopic, Questions, Actions

  return (
    <Box sx={{ maxWidth: 1600, mx: "auto", px: { xs: 1, sm: 1.5 }, pb: 6 }}>
      {/* Page header */}
      <Box sx={{ mb: 1.5, pt: 1 }}>
        <Typography variant="h5" sx={{ fontWeight: 700, mb: 0.25, fontSize: { xs: "1.15rem", sm: "1.5rem" } }}>
          {isParent ? "Work Sheet Results" : "Work Sheets"}
        </Typography>
        <Typography color="text.secondary" variant="caption" sx={{ display: "block" }}>
          {isParent
            ? "View your child's work sheet results and subject area analysis."
            : "Do work sheets online or upload a scanned answer sheet."}
        </Typography>
      </Box>

      {/* Tabs */}
      <Tabs
        value={tab}
        onChange={(_, v) => setTab(v)}
        variant="scrollable"
        scrollButtons="auto"
        allowScrollButtonsMobile
        sx={{ mb: 1.5, minHeight: 40, "& .MuiTab-root": { minHeight: 40, py: 0.5 }, borderBottom: 1, borderColor: "divider" }}
      >
        <Tab label="Work Sheets" value={TAB_WORKSHEETS} icon={<QuizIcon />} iconPosition="start" />
        <Tab label={isParent ? "Student Results" : "My Results"} value={TAB_RESULTS} icon={<BarChartIcon />} iconPosition="start" />
      </Tabs>

      {isTestMode && (
        <Alert severity="info" sx={{ mb: 2, borderRadius: 2, fontWeight: 600 }}>
          PRACTICE MODE — Practice freely. Answers will not be graded.
        </Alert>
      )}

      {/* ── Continue Monitoring Banner ─────────────────────────────────────── */}
      {pendingMonitorSession && !uploadDialogOpen && (
        <Card
          sx={{
            mb: 3,
            borderRadius: 2,
            bgcolor: "rgba(102,126,234,0.07)",
            border: "1px solid rgba(102,126,234,0.3)",
            boxShadow: "0 2px 8px rgba(102,126,234,0.1)",
          }}
        >
          <CardContent sx={{ p: 2, "&:last-child": { pb: 2 } }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, flexWrap: "wrap" }}>
              <RestoreOutlinedIcon color="primary" />
              <Box sx={{ flex: 1 }}>
                <Typography variant="body2" sx={{ fontWeight: 700 }}>
                  Explanation generation was paused
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  A previous upload is still waiting for explanations to be generated.
                </Typography>
              </Box>
              <Box sx={{ display: "flex", gap: 1 }}>
                <Button
                  size="small"
                  variant="contained"
                  startIcon={<PlayCircleOutlineIcon />}
                  onClick={handleContinueMonitoring}
                  sx={{
                    background: "linear-gradient(90deg, #667eea 0%, #764ba2 100%)",
                    fontWeight: 600,
                    borderRadius: 1.5,
                  }}
                >
                  Continue Monitoring
                </Button>
                <Button
                  size="small"
                  variant="outlined"
                  color="error"
                  onClick={handleDismissMonitorSession}
                  sx={{ borderRadius: 1.5 }}
                >
                  Dismiss
                </Button>
              </Box>
            </Box>
          </CardContent>
        </Card>
      )}

      {/* ── RESULTS TAB ────────────────────────────────────────────────────── */}
      {tab === TAB_RESULTS && (
        <>
          {isParent && !isTesterAI && (
            <FormControl fullWidth sx={{ mb: 3, maxWidth: 320 }}>
              <InputLabel>Select Student</InputLabel>
              <Select label="Select Student" value={selectedStudentId} onChange={(e) => { setSelectedStudentId(e.target.value); setSelectedTestId(""); setResult(null); setResultTestMeta(null); }}>
                <MenuItem value=""><em>Choose student</em></MenuItem>
                {students.map((s) => <MenuItem key={s.id} value={s.id}>{s.label}</MenuItem>)}
              </Select>
            </FormControl>
          )}

          {/* Weak areas (parent) */}
          {isParent && weakAreasBySubject.length > 0 && (
            <Card sx={{ borderRadius: 3, mb: 3, boxShadow: "0 4px 20px rgba(0,0,0,0.08)", border: "1px solid #fecaca" }}>
              <CardContent>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.5 }}>
                  <Box sx={{ width: 4, height: 20, borderRadius: 1, bgcolor: "#dc2626" }} />
                  <Typography variant="h6" sx={{ fontWeight: 700, color: "#991b1b" }}>
                    Subject Area Analysis — Weak Areas
                  </Typography>
                </Box>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Areas where your child may need more practice, based on work sheet results.
                </Typography>
                <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2 }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow sx={{ background: "#fef2f2" }}>
                        <TableCell sx={{ fontWeight: 700 }}>Subject / Strand</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Subtopic</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 700 }}>Score</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 700 }}>Attempts</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {weakAreasBySubject.slice(0, 10).map((row) => (
                        <TableRow key={row.key} sx={{ "&:hover": { bgcolor: "#fef2f2" } }}>
                          <TableCell>
                            <Typography sx={{ fontWeight: 600 }}>{row.subject || "—"}</Typography>
                            {row.strand && <Typography variant="caption" color="text.secondary">{row.strand}</Typography>}
                          </TableCell>
                          <TableCell>{row.subtopic || "—"}</TableCell>
                          <TableCell align="right">
                            <Chip size="small" label={`${row.percentage}%`} color={row.percentage >= 50 ? "success" : "error"} sx={{ fontWeight: 700 }} />
                          </TableCell>
                          <TableCell align="right">{row.attempts}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </CardContent>
            </Card>
          )}

          {/* All attempts table */}
          <Card sx={{ borderRadius: 3, mb: 3, boxShadow: "0 4px 20px rgba(0,0,0,0.08)" }}>
            <CardContent>
              <Typography variant="h6" sx={{ fontWeight: 700, mb: 0.5 }}>
                {isParent ? "Student Work Sheet Attempts" : "My Work Sheet Attempts"}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {isParent ? "Filter by work sheet to view sweep history and previous attempts." : "Click Review for a full question-by-question breakdown. Filter by worksheet to see sweep history."}
              </Typography>

              {/* Filter bar */}
              {!isParent || isTesterAI || selectedStudentId ? (
                <Box sx={{ display: "flex", gap: 2, mb: 2, flexWrap: "wrap", alignItems: "center" }}>
                  <TextField
                    size="small"
                    placeholder="Search work sheets…"
                    value={resultsSearch}
                    onChange={(e) => setResultsSearch(e.target.value)}
                    sx={{ flex: { xs: "1 1 100%", sm: "0 0 auto" }, minWidth: { sm: 200 }, maxWidth: { xs: "100%", sm: "none" } }}
                    InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }}
                  />
                  <FormControl size="small" sx={{ flex: { xs: "1 1 100%", sm: "0 0 auto" }, minWidth: { sm: 220 }, maxWidth: { xs: "100%", sm: "none" } }}>
                    <InputLabel>All Work Sheets</InputLabel>
                    <Select label="All Work Sheets" value={resultsTestFilter} onChange={(e) => {
                      const val = e.target.value;
                      setResultsTestFilter(val);
                      if (val) {
                        setSelectedTestId(val);
                        fetchAttemptHistory(val, isParent ? selectedStudentId : undefined);
                        fetchRetakeHistory(val, isParent ? selectedStudentId : undefined);
                      } else {
                        setSelectedTestId("");
                      }
                    }}>
                      <MenuItem value=""><em>All work sheets</em></MenuItem>
                      {uniqueResultsTests.map((t) => <MenuItem key={t.id} value={t.id}>{t.name}</MenuItem>)}
                    </Select>
                  </FormControl>
                  {(resultsSearch || resultsTestFilter) && (
                    <Button size="small" variant="outlined" onClick={() => { setResultsSearch(""); setResultsTestFilter(""); setSelectedTestId(""); }} sx={{ textTransform: "none", borderRadius: 2 }}>
                      Clear
                    </Button>
                  )}
                  {isTestMode && (
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1, ml: "auto" }}>
                      <Typography variant="body2" color="text.secondary">Show practice results</Typography>
                      <input type="checkbox" checked={showPracticeResults} onChange={(e) => { setShowPracticeResults(e.target.checked); setAttemptsPage(1); }} />
                    </Box>
                  )}
                </Box>
              ) : null}

              {isParent && !isTesterAI && !selectedStudentId ? (
                <Box sx={{ textAlign: "center", py: 6, border: "2px dashed", borderColor: "divider", borderRadius: 2 }}>
                  <SchoolIcon sx={{ fontSize: 48, color: "text.disabled", mb: 1 }} />
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>Select a Student</Typography>
                  <Typography color="text.secondary" sx={{ mt: 0.5, fontSize: "0.9rem" }}>Choose a student above to view their work sheet results.</Typography>
                </Box>
              ) : loadingAllAttempts ? (
                <LinearProgress sx={{ borderRadius: 2 }} />
              ) : filteredAllAttempts.length > 0 ? (
                <>
                  <TableContainer component={Paper} sx={{ borderRadius: 2, overflow: "auto" }} variant="outlined">
                    <Table size="small" stickyHeader>
                      <TableHead>
                        <TableRow sx={{ background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)" }}>
                          <TableCell sx={{ fontWeight: 700, color: "white", bgcolor: "transparent !important" }}>Work Sheet</TableCell>
                          {isParent && <TableCell sx={{ fontWeight: 700, color: "white", bgcolor: "transparent !important" }}>Subject / Topic</TableCell>}
                          <TableCell sx={{ fontWeight: 700, color: "white", bgcolor: "transparent !important" }}>Date</TableCell>
                          <TableCell sx={{ fontWeight: 700, color: "white", bgcolor: "transparent !important" }}>Source</TableCell>
                          <TableCell sx={{ fontWeight: 700, color: "white", bgcolor: "transparent !important" }}>Score</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 700, color: "white", bgcolor: "transparent !important" }}>Actions</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {filteredAllAttempts.map((row) => (
                          <TableRow
                            key={`${row.test_id}-${row.id}`}
                            sx={{ "&:hover": { bgcolor: "#f8f9fb" }, bgcolor: selectedTestId === String(row.test_id) ? "#f3f0ff" : "inherit" }}
                          >
                            <TableCell>
                              <Typography sx={{ fontWeight: 600, fontSize: "0.9rem" }}>{row.test_name || `Test #${row.test_id}`}</Typography>
                              <Box sx={{ display: "flex", gap: 0.5, mt: 0.5 }}>
                                <Chip size="small" label={`Attempt ${row.attempt_count ?? 1}`} variant="outlined" sx={{ height: 20, fontSize: "0.7rem" }} />
                                {row.is_test_mode && <Chip size="small" label="PRACTICE" color="info" sx={{ height: 20, fontSize: "0.65rem", fontWeight: 700 }} />}
                              </Box>
                            </TableCell>
                            {isParent && (
                              <TableCell>
                                <Typography variant="body2" sx={{ fontWeight: 500 }}>{row.subject || "—"}</Typography>
                                {(row.strand || row.subtopic) && <Typography variant="caption" color="text.secondary">{[row.strand, row.subtopic].filter(Boolean).join(" › ")}</Typography>}
                              </TableCell>
                            )}
                            <TableCell>
                              <Typography variant="body2" sx={{ fontSize: "0.82rem" }}>{formatAttemptDate(row.created_at)}</Typography>
                            </TableCell>
                            <TableCell>
                              <Chip size="small" label={row.source === "online" ? "Online" : "Upload"} color={row.source === "online" ? "primary" : "default"} variant="outlined" sx={{ fontSize: "0.75rem" }} />
                            </TableCell>
                            <TableCell>
                              <Chip size="small" label={`${row.percentage}%`} color={row.percentage >= 50 ? "success" : "error"} sx={{ fontWeight: 700 }} />
                            </TableCell>
                            <TableCell align="right">
                              <Stack direction="row" spacing={0.75} justifyContent="flex-end" flexWrap="wrap" useFlexGap>
                                <Button
                                  size="small"
                                  variant="contained"
                                  onClick={() => handleNavigateReview(row.test_id, row.id)}
                                  sx={{ textTransform: "none", borderRadius: 2, fontWeight: 700, whiteSpace: "nowrap", background: "linear-gradient(90deg, #667eea 0%, #764ba2 100%)" }}
                                >
                                  Review
                                </Button>
                                <Button
                                  size="small"
                                  variant="outlined"
                                  onClick={() => isValidTestId(row.test_id) && navigate(`/${prefix}/work-sheet/${row.test_id}/sweep-history`, { state: { testName: row.test_name } })}
                                  sx={{ textTransform: "none", borderRadius: 2, whiteSpace: "nowrap" }}
                                  color="secondary"
                                >
                                  Sweep History
                                </Button>
                              </Stack>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                  <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mt: 2 }}>
                    <Button variant="outlined" size="small" disabled={attemptsPagination.current_page <= 1} onClick={() => setAttemptsPage((p) => Math.max(1, p - 1))} sx={{ borderRadius: 2 }}>Previous</Button>
                    <Typography variant="body2" color="text.secondary">Page {attemptsPagination.current_page} of {attemptsPagination.last_page}</Typography>
                    <Button variant="outlined" size="small" disabled={attemptsPagination.current_page >= attemptsPagination.last_page} onClick={() => setAttemptsPage((p) => p + 1)} sx={{ borderRadius: 2 }}>Next</Button>
                  </Box>
                </>
              ) : (
                <Box sx={{ textAlign: "center", py: 6, border: "2px dashed", borderColor: "divider", borderRadius: 2 }}>
                  <BarChartIcon sx={{ fontSize: 48, color: "text.disabled", mb: 1 }} />
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    {allAttempts.length === 0 ? "No Results Yet" : "No matches"}
                  </Typography>
                  <Typography color="text.secondary" sx={{ mt: 0.5, fontSize: "0.9rem" }}>
                    {allAttempts.length === 0
                      ? (isParent ? "This student has not completed any work sheets yet." : "Complete a work sheet to see your results here.")
                      : "No attempts match your filters."}
                  </Typography>
                  {(resultsSearch || resultsTestFilter) && (
                    <Button sx={{ mt: 1.5 }} size="small" variant="outlined" onClick={() => { setResultsSearch(""); setResultsTestFilter(""); setSelectedTestId(""); }}>Clear Filters</Button>
                  )}
                </Box>
              )}
            </CardContent>
          </Card>
        </>
      )}

      {/* ── WORKSHEETS TAB ─────────────────────────────────────────────────── */}
      {tab === TAB_WORKSHEETS && (
        <Box>
          {!isParent && (
            <Alert
              severity="info"
              icon={false}
              sx={{ mb: 1.5, py: 0.5, borderRadius: 2, "& .MuiAlert-message": { width: "100%", py: 0.5 } }}
            >
              <Stack spacing={0.25}>
                {[
                  { label: "Download ▾", text: "Choose Question Paper (questions to read) or Answer Sheet (blank sheet to fill in and scan)." },
                  { label: "Do Online", text: "Take the work sheet in your browser and submit answers directly." },
                  { label: "Upload", text: "Print the answer sheet, fill it, scan and upload (PDF, PNG, JPG, max 20 MB)." },
                  { label: "Tip", text: "Keep the four corner fiducials visible in scans for best OCR accuracy." },
                  { label: "Sweep", text: "After upload or online submission, use Sweep to retake wrong questions." },
                ].map(({ label, text }) => (
                  <Typography key={label} variant="caption" color="text.secondary" sx={{ lineHeight: 1.4 }}>
                    <Typography component="span" variant="caption" sx={{ fontWeight: 700, color: "text.primary" }}>{label}:</Typography> {text}
                  </Typography>
                ))}
              </Stack>
            </Alert>
          )}
          {/* Parent student select (real parents only — testers have no linked children) */}
          {isParent && !isTesterAI && (
            <FormControl fullWidth sx={{ mb: 3, maxWidth: 320 }}>
              <InputLabel>Select Student</InputLabel>
              <Select label="Select Student" value={selectedStudentId} onChange={(e) => { setSelectedStudentId(e.target.value); setSelectedTestId(""); setResult(null); setSelectedCourseId(""); setSelectedSubjectId(""); setSelectedTopics([]); setSelectedWeekId(""); setSelectedDifficulties([]); }}>
                <MenuItem value=""><em>Choose student</em></MenuItem>
                {students.map((s) => <MenuItem key={s.id} value={s.id}>{s.label}</MenuItem>)}
              </Select>
            </FormControl>
          )}

          {/* Filter bar */}
          <Paper elevation={0} sx={{ display: "flex", gap: 1.5, mb: 1.5, p: 1, flexWrap: "wrap", alignItems: "center", border: "1px solid", borderColor: "divider", borderRadius: 2 }}>
            <TextField
              size="small"
              placeholder="Search work sheets…"
              value={cardSearch}
              onChange={(e) => setCardSearch(e.target.value)}
              sx={{ flex: { xs: "1 1 100%", sm: 1 }, minWidth: { sm: 200 }, maxWidth: "100%" }}
              InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }}
            />
            {showCourseFilter && (
              <FormControl size="small" sx={{ flex: { xs: "1 1 100%", sm: "0 0 auto" }, minWidth: { sm: 160 }, maxWidth: { xs: "100%", sm: "none" } }} disabled={isParent && !isTesterAI && !selectedStudentId}>
                <InputLabel>Course</InputLabel>
                <Select label="Course" value={selectedCourseId} onChange={(e) => setSelectedCourseId(e.target.value)}>
                  <MenuItem value=""><em>All</em></MenuItem>
                  {courseOptions.map((c) => (
                    <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
            <FormControl size="small" sx={{ flex: { xs: "1 1 100%", sm: "0 0 auto" }, minWidth: { sm: 150 }, maxWidth: { xs: "100%", sm: "none" } }} disabled={isParent && !isTesterAI && !selectedStudentId}>
              <InputLabel>Subject</InputLabel>
              <Select label="Subject" value={selectedSubjectId} onChange={(e) => setSelectedSubjectId(e.target.value)}>
                <MenuItem value=""><em>All</em></MenuItem>
                {subjectOptions.map((s) => (
                  <MenuItem key={s.id} value={s.id}>{s.name}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ flex: { xs: "1 1 100%", sm: "0 0 auto" }, minWidth: { sm: 180 }, maxWidth: { xs: "100%", sm: "none" } }} disabled={isParent && !isTesterAI && !selectedStudentId}>
              <InputLabel>Topic</InputLabel>
              <Select
                multiple
                label="Topic"
                value={selectedTopics}
                onChange={(e) => setSelectedTopics(typeof e.target.value === "string" ? e.target.value.split(",") : e.target.value)}
                renderValue={(sel) =>
                  sel.length === 0 ? <em>All</em> :
                  sel.length === 1 ? sel[0] :
                  `${sel.length} Topics`
                }
              >
                {topicOptions.length === 0
                  ? <MenuItem disabled><em>No topics available</em></MenuItem>
                  : topicOptions.map((opt) => (
                      <MenuItem key={opt.name} value={opt.name} dense>
                        <Checkbox checked={selectedTopics.includes(opt.name)} size="small" />
                        <ListItemText primary={opt.name} />
                      </MenuItem>
                    ))
                }
              </Select>
            </FormControl>
            {showWeekFilter && (
              <FormControl size="small" sx={{ flex: { xs: "1 1 100%", sm: "0 0 auto" }, minWidth: { sm: 240 }, maxWidth: { xs: "100%", sm: "none" } }} disabled={isParent && !isTesterAI && !selectedStudentId}>
                <InputLabel>Week</InputLabel>
                <Select label="Week" value={selectedWeekId} onChange={(e) => setSelectedWeekId(e.target.value)} renderValue={(v) => {
                  if (!v) return <em>All</em>;
                  const opt = weekOptions.find((w) => w.id === v);
                  return opt?.label || v;
                }}>
                  <MenuItem value=""><em>All</em></MenuItem>
                  {weekOptions.map((w) => (
                    <MenuItem key={w.id} value={w.id}>{w.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, flex: { xs: "1 1 100%", sm: "0 0 auto" }, flexWrap: "wrap" }}>
              <Typography variant="caption" sx={{ fontWeight: 700, color: "text.secondary", mr: 0.25 }}>Difficulty</Typography>
              {DIFFICULTY_LEVELS.map((lvl) => {
                const active = selectedDifficulties.includes(lvl.value);
                return (
                  <Chip
                    key={lvl.value}
                    label={`${"🌶️".repeat(lvl.chilies)} ${lvl.value}`}
                    size="small"
                    clickable
                    disabled={isParent && !isTesterAI && !selectedStudentId}
                    onClick={() => setSelectedDifficulties((prev) => (prev.includes(lvl.value) ? prev.filter((v) => v !== lvl.value) : [...prev, lvl.value]))}
                    sx={{
                      fontWeight: 600,
                      bgcolor: active ? lvl.bg : "action.hover",
                      color: active ? lvl.color : "text.secondary",
                      border: "1px solid",
                      borderColor: active ? lvl.color : "divider",
                      "&:hover": { bgcolor: active ? lvl.bg : "action.selected" },
                    }}
                  />
                );
              })}
            </Box>
            {activeFilterCount > 0 && (
              <Button size="small" variant="outlined" color="error" onClick={() => { setCardSearch(""); setSelectedCourseId(""); setSelectedSubjectId(""); setSelectedTopics([]); setSelectedWeekId(""); setSelectedDifficulties([]); }} sx={{ textTransform: "none", borderRadius: 2, whiteSpace: "nowrap" }}>
                Clear ({activeFilterCount})
              </Button>
            )}
          </Paper>

          {/* Table */}
          <TableContainer component={Paper} sx={{ borderRadius: 3, boxShadow: "0 4px 20px rgba(0,0,0,0.06)" }}>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)" }}>
                  <TableCell sx={{ color: "white", fontWeight: 700, minWidth: 300, width: "32%" }}>Name</TableCell>
                  {showCourseFilter && <TableCell sx={{ color: "white", fontWeight: 700 }}>Course</TableCell>}
                  <TableCell sx={{ color: "white", fontWeight: 700 }}>Subject</TableCell>
                  <TableCell sx={{ color: "white", fontWeight: 700, maxWidth: 200 }}>Topic</TableCell>
                  <TableCell sx={{ color: "white", fontWeight: 700, maxWidth: 160 }}>Subtopic</TableCell>
                  <TableCell sx={{ color: "white", fontWeight: 700 }} align="center">Questions</TableCell>
                  <TableCell sx={{ color: "white", fontWeight: 700 }} align="center">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {fetchingTests
                  ? [...Array(5)].map((_, i) => (
                      <TableRow key={i}>
                        {[...Array(worksheetTableColumnCount)].map((__, j) => (
                          <TableCell key={j}><Skeleton /></TableCell>
                        ))}
                      </TableRow>
                    ))
                  : fetchTestsError
                  ? (
                      <TableRow>
                        <TableCell colSpan={worksheetTableColumnCount}>
                          <Box sx={{ textAlign: "center", py: 4 }}>
                            <Alert severity="error" sx={{ mb: 2, maxWidth: 420, mx: "auto" }}>
                              Unable to load work sheets. Please check your connection.
                            </Alert>
                            <Button variant="contained" onClick={() => fetchTests(isParent ? selectedStudentId : undefined)}>
                              Retry
                            </Button>
                          </Box>
                        </TableCell>
                      </TableRow>
                    )
                  : filteredTests.length === 0
                  ? (
                      <TableRow>
                        <TableCell colSpan={worksheetTableColumnCount}>
                          <Box sx={{ textAlign: "center", py: 6 }}>
                            <QuizIcon sx={{ fontSize: 48, color: "text.disabled", mb: 1 }} />
                            <Typography color="text.secondary">
                              {tests.length === 0
                                ? (isParent && !isTesterAI && !selectedStudentId ? "Select a student to see their work sheets." : "No work sheets available for your courses.")
                                : "No work sheets match your filters."}
                            </Typography>
                            {activeFilterCount > 0 && (
                              <Button sx={{ mt: 1.5 }} size="small" variant="outlined" color="error" onClick={() => { setCardSearch(""); setSelectedCourseId(""); setSelectedSubjectId(""); setSelectedTopics([]); setSelectedWeekId(""); setSelectedDifficulties([]); }}>Clear Filters</Button>
                            )}
                          </Box>
                        </TableCell>
                      </TableRow>
                    )
                  : paginatedFilteredTests.map((test) => {
                      const isActive = isWorksheetActive(test);
                      const strandLabel =
                        test.strand ||
                        test.topic_name ||
                        test.topic ||
                        (Array.isArray(test.master_topic_names) ? test.master_topic_names.filter(Boolean).join(", ") : "");
                      const subjectLabel = test.subject || test.subject_name || "—";
                      const difficultyLevel = DIFFICULTY_LEVELS.find((lvl) => lvl.value === normalizeDifficulty(test.difficulty));
                      const subtopicLabel =
                        test.subtopic ||
                        test.subtopic_name ||
                        test.sub_topic ||
                        (Array.isArray(test.master_subtopic_names) ? test.master_subtopic_names.filter(Boolean).join(", ") : "") ||
                        "—";
                      return (
                      <TableRow
                        key={test.id}
                        hover
                        sx={{
                          "&:hover": { bgcolor: "action.hover" },
                          opacity: isActive ? 1 : 0.6,
                        }}
                      >
                        <TableCell sx={{ minWidth: 300, width: "32%" }}>
                          <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap", mb: 0.5 }}>
                            <Typography sx={{ fontWeight: 600, fontSize: "0.9rem" }}>{test.name}</Typography>
                            {!isActive && <Chip label="Inactive" size="small" color="default" variant="outlined" />}
                          </Box>
                          {difficultyLevel && (
                            <Chip
                              label={`${"🌶️".repeat(difficultyLevel.chilies)} ${difficultyLevel.value}`}
                              size="small"
                              sx={{ height: 20, fontSize: "0.7rem", fontWeight: 700, bgcolor: difficultyLevel.bg, color: difficultyLevel.color, mb: 0.5 }}
                            />
                          )}
                        </TableCell>
                        {showCourseFilter && (
                          <TableCell>
                            {test.course_name
                              ? <Chip label={test.course_name} size="small" color="primary" variant="outlined" />
                              : <Typography variant="body2" color="text.disabled">—</Typography>}
                          </TableCell>
                        )}
                        <TableCell>
                          <Typography variant="body2">{subjectLabel}</Typography>
                        </TableCell>
                        <TableCell sx={{ maxWidth: 200 }}>
                          <Tooltip title={strandLabel || ""} placement="top" enterDelay={400}>
                            <Typography variant="body2" color="text.secondary" noWrap sx={{ maxWidth: 200 }}>{strandLabel || "—"}</Typography>
                          </Tooltip>
                        </TableCell>
                        <TableCell sx={{ maxWidth: 160 }}>
                          <Tooltip title={subtopicLabel !== "—" ? subtopicLabel : ""} placement="top" enterDelay={400}>
                            <Typography variant="body2" color="text.secondary" noWrap sx={{ maxWidth: 160 }}>{subtopicLabel}</Typography>
                          </Tooltip>
                        </TableCell>
                        <TableCell align="center">
                          {test.question_count != null
                            ? <Chip label={test.question_count} size="small" variant="outlined" />
                            : <Typography variant="body2" color="text.disabled">—</Typography>}
                        </TableCell>
                        <TableCell align="center">
                          <Box sx={{ display: "flex", gap: 1, justifyContent: "center", alignItems: "center" }}>
                            {!isParent && (
                              <Button
                                size="small"
                                variant="contained"
                                startIcon={<PlayArrowIcon />}
                                onClick={() => handleDoOnline(test)}
                                disabled={!isActive}
                                sx={{ textTransform: "none", fontWeight: 700, borderRadius: 2, whiteSpace: "nowrap", background: "linear-gradient(90deg, #667eea 0%, #764ba2 100%)" }}
                              >
                                Do Online
                              </Button>
                            )}
                            <Button
                              size="small"
                              variant="outlined"
                              endIcon={
                                (downloadingKey[`${test.id}-question-paper`] || downloadingKey[`${test.id}-answer-sheet`] || downloadingKey[`${test.id}-with-answers`])
                                  ? <CircularProgress size={12} /> : <ArrowDropDownIcon />
                              }
                              onClick={(e) => setDownloadMenuAnchor((prev) => ({ ...prev, [test.id]: e.currentTarget }))}
                              disabled={!isActive || Boolean(downloadingKey[`${test.id}-question-paper`] || downloadingKey[`${test.id}-answer-sheet`] || downloadingKey[`${test.id}-with-answers`])}
                              sx={{ textTransform: "none", borderRadius: 2, whiteSpace: "nowrap" }}
                            >
                              <DownloadIcon sx={{ fontSize: 15, mr: 0.5 }} /> Download
                            </Button>
                            <Menu
                              anchorEl={downloadMenuAnchor[test.id]}
                              open={Boolean(downloadMenuAnchor[test.id])}
                              onClose={() => setDownloadMenuAnchor((prev) => { const n = { ...prev }; delete n[test.id]; return n; })}
                              anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                              transformOrigin={{ vertical: "top", horizontal: "right" }}
                            >
                              <MenuItem onClick={() => handleDownloadVariant(test, "question-paper")} disabled={!isActive || Boolean(downloadingKey[`${test.id}-question-paper`])}>
                                <DownloadIcon fontSize="small" sx={{ mr: 1, color: "text.secondary" }} /> Question Paper
                              </MenuItem>
                              <MenuItem onClick={() => handleDownloadVariant(test, "answer-sheet")} disabled={!isActive || Boolean(downloadingKey[`${test.id}-answer-sheet`])}>
                                <DownloadIcon fontSize="small" sx={{ mr: 1, color: "text.secondary" }} /> Answer Sheet
                              </MenuItem>
                              {canDownloadAnswerKey && allowAnswerKeyDownload && (
                                <MenuItem onClick={() => handleDownloadVariant(test, "with-answers")} disabled={!isActive || Boolean(downloadingKey[`${test.id}-with-answers`])}>
                                  <DownloadIcon fontSize="small" sx={{ mr: 1, color: "secondary.main" }} /> Answer key
                                </MenuItem>
                              )}
                            </Menu>
                            {!isParent && (
                              <Tooltip title={isActive ? "Upload Answer Sheet" : "Inactive work sheet"}>
                                <IconButton
                                  size="small"
                                  onClick={() => handleOpenUpload(test)}
                                  disabled={!isActive}
                                  sx={{ border: "1px solid", borderColor: "divider", borderRadius: 2, width: 34, height: 34, "&:hover": { bgcolor: "action.hover" } }}
                                >
                                  <CloudUploadIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            )}
                          </Box>
                        </TableCell>
                      </TableRow>
                    )})}
              </TableBody>
            </Table>
          </TableContainer>
          {filteredTests.length > 0 && (
            <TablePagination
              component="div"
              count={filteredTests.length}
              page={worksheetPage}
              onPageChange={(_, newPage) => setWorksheetPage(newPage)}
              rowsPerPage={worksheetRowsPerPage}
              onRowsPerPageChange={(e) => { setWorksheetRowsPerPage(parseInt(e.target.value, 10)); setWorksheetPage(0); }}
              rowsPerPageOptions={[10, 25, 50, 100]}
            />
          )}
        </Box>
      )}

      {/* ── SWEEP PROGRESS TRACKER ─────────────────────────────────────────── */}
      {tab !== TAB_WORKSHEETS && selectedTestId && retakeHistory && retakeHistory.by_upload_attempt && retakeHistory.by_upload_attempt.length > 0 && (
        <Box sx={{ mt: 4 }}>
          {loadingRetakeHistory && <LinearProgress sx={{ mb: 2, borderRadius: 2 }} />}
          <Card
            sx={{
              borderRadius: 3,
              mb: 3,
              background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
              color: "white",
              boxShadow: "0 8px 32px rgba(102,126,234,0.35)",
            }}
          >
            <CardContent
              sx={{ cursor: "pointer", userSelect: "none", pb: showSweepTracker ? 1 : undefined }}
              onClick={() => setShowSweepTracker((p) => !p)}
            >
              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                  <AutoAwesomeIcon sx={{ opacity: 0.9 }} />
                  <Typography variant="h6" sx={{ fontWeight: 700 }}>Sweep Progress Tracker</Typography>
                </Box>
                <ExpandMoreIcon sx={{ transform: showSweepTracker ? "rotate(180deg)" : "none", transition: "transform 0.25s", opacity: 0.85 }} />
              </Box>
            </CardContent>
            <Collapse in={showSweepTracker}>
              <CardContent sx={{ pt: 0 }}>
                <Typography variant="body2" sx={{ mb: 2.5, opacity: 0.9 }}>
                  Each answer sheet upload allows up to <strong>{retakeHistory.max_swapes_per_upload || 4}</strong> Sweep sessions to improve your score.
                </Typography>
                {/* Sessions progress bar */}
                <Box sx={{ mb: 3, bgcolor: "rgba(255,255,255,0.1)", borderRadius: 2, p: 2 }}>
                  <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}>
                    <Typography variant="body2" sx={{ opacity: 0.9, fontWeight: 600 }}>Latest Upload Sweep Usage</Typography>
                    <Typography variant="body2" sx={{ opacity: 0.9, fontWeight: 700 }}>
                      {retakeHistory.latest_swape_sessions_used || 0} / {retakeHistory.max_swapes_per_upload || 4}
                    </Typography>
                  </Box>
                  <LinearProgress
                    variant="determinate"
                    value={((retakeHistory.latest_swape_sessions_used || 0) / (retakeHistory.max_swapes_per_upload || 4)) * 100}
                    sx={{ height: 8, borderRadius: 4, bgcolor: "rgba(255,255,255,0.2)", "& .MuiLinearProgress-bar": { bgcolor: "white" } }}
                  />
                </Box>
                <Grid container spacing={2}>
                  {[
                    { label: "Total Uploads", value: retakeHistory.by_upload_attempt.length, caption: "answer sheets submitted" },
                    { label: "Sessions Used", value: `${retakeHistory.latest_swape_sessions_used || 0}/${retakeHistory.max_swapes_per_upload || 4}`, caption: "for latest upload" },
                    { label: "Sweeps Remaining", value: retakeHistory.latest_swape_sessions_remaining || 0, caption: "for latest upload" },
                    { label: "Total Sessions", value: retakeHistory.by_upload_attempt.reduce((sum, u) => sum + u.swape_sessions_used, 0), caption: "across all uploads" },
                  ].map(({ label, value, caption }) => (
                    <Grid item xs={6} sm={4} md={3} key={label}>
                      <Paper sx={{ p: 2, textAlign: "center", background: "rgba(255,255,255,0.15)", backdropFilter: "blur(10px)", borderRadius: 2, border: "1px solid rgba(255,255,255,0.2)" }}>
                        <Typography variant="body2" sx={{ opacity: 0.9, mb: 0.5 }}>{label}</Typography>
                        <Typography variant="h4" sx={{ fontWeight: 800 }}>{value}</Typography>
                        <Typography variant="caption" sx={{ opacity: 0.75 }}>{caption}</Typography>
                      </Paper>
                    </Grid>
                  ))}
                </Grid>
              </CardContent>
            </Collapse>
          </Card>
        </Box>
      )}

      {/* ── PREVIOUS ATTEMPTS ─────────────────────────────────────────────── */}
      {tab !== TAB_WORKSHEETS && selectedTestId && (
        <Box sx={{ mt: selectedTestId && retakeHistory?.by_upload_attempt?.length > 0 ? 0 : 4 }}>
          <Card sx={{ borderRadius: 3, mb: 3, boxShadow: "0 4px 20px rgba(0,0,0,0.08)" }}>
            <CardContent sx={{ cursor: "pointer", userSelect: "none" }} onClick={() => setShowPreviousAttempts((p) => !p)}>
              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <Box>
                  <Typography variant="h6" sx={{ fontWeight: 700 }}>Previous Attempts</Typography>
                  {attempts.length > 0 && (
                    <Typography variant="caption" color="text.secondary">
                      {attempts.length} attempt{attempts.length !== 1 ? "s" : ""}
                    </Typography>
                  )}
                </Box>
                <ExpandMoreIcon sx={{ transform: showPreviousAttempts ? "rotate(180deg)" : "none", transition: "transform 0.25s", color: "text.secondary" }} />
              </Box>
            </CardContent>
            <Collapse in={showPreviousAttempts}>
              <Divider />
              <CardContent sx={{ pt: 2 }}>
                {loadingAttempts && <LinearProgress sx={{ mb: 2, borderRadius: 2 }} />}
                {!loadingAttempts && attempts.length === 0 && (
                  <Typography variant="body2" color="text.secondary">No previous uploads yet.</Typography>
                )}
                {!loadingAttempts && attempts.length > 0 && (
                  <TableContainer component={Paper} sx={{ borderRadius: 2, overflow: "auto" }} variant="outlined">
                    <Table size="small" stickyHeader>
                      <TableHead>
                        <TableRow>
                          {["Attempt", "Date", "Upload Score", "Cumulative", "Incorrect", "Sweep Sessions", "Engine", "Actions"].map((h) => (
                            <TableCell key={h} sx={{ fontWeight: 700, bgcolor: "#f8f9fb", fontSize: "0.8rem" }}>{h}</TableCell>
                          ))}
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {(() => {
                          const latestAttemptId = attempts.reduce((best, a) =>
                            (a.attempt_count ?? 0) > (best?.attempt_count ?? -1) ? a : best, null)?.id;
                          return attempts.map((attempt) => {
                          const stats = getAttemptStats(attempt);
                          const retakeInProgress = Boolean(retakeLoading[attempt.id]);
                          const uploadData = retakeHistory?.by_upload_attempt?.find((u) => u.upload_attempt_count === attempt.attempt_count);
                          const swapeSessionsUsed = uploadData?.swape_sessions_used || 0;
                          const swapeSessionsRemaining = uploadData?.swape_sessions_remaining || 4;
                          const maxSwapes = retakeHistory?.max_swapes_per_upload || 4;
                          const isLatest = attempt.id === latestAttemptId;
                          const showRetakeButton = !isParent && isLatest && stats.incorrectAnswers > 0 && swapeSessionsRemaining > 0;
                          const allSwapesExhausted = !isParent && isLatest && stats.incorrectAnswers > 0 && swapeSessionsRemaining === 0;
                          const cumulativePercentage = uploadData?.cumulative_percentage ?? stats.percentage;
                          const cumulativeCorrect = uploadData?.cumulative_correct_answers ?? stats.correctAnswers;
                          const swapeCorrected = uploadData?.swape_corrected_count || 0;
                          const swapeSessions = uploadData?.swape_sessions || [];
                          return (
                            <TableRow key={`${attempt.id}-${attempt.attempt_count || "attempt"}`} sx={{ "&:hover": { bgcolor: "#f8f9fb" } }}>
                              <TableCell>
                                <Chip label={`Attempt ${attempt.attempt_count ?? "—"}`} size="small" color="primary" sx={{ fontWeight: 700 }} />
                              </TableCell>
                              <TableCell sx={{ fontSize: "0.8rem" }}>{formatAttemptTimestamp(attempt?.created_at)}</TableCell>
                              <TableCell>
                                <Typography sx={{ fontWeight: 700, color: "#1a237e", fontSize: "0.9rem" }}>{stats.percentage}%</Typography>
                                <Typography variant="caption" color="text.secondary">{stats.correctAnswers}/{stats.gradableTotal}</Typography>
                              </TableCell>
                              <TableCell>
                                <Typography sx={{ fontWeight: 800, color: cumulativePercentage === 100 ? "#2e7d32" : "#1a237e", fontSize: "1rem" }}>
                                  {cumulativePercentage}%
                                </Typography>
                                <Typography variant="caption" color="text.secondary">{cumulativeCorrect}/{stats.gradableTotal}</Typography>
                                {swapeCorrected > 0 && <Chip label={`+${swapeCorrected} via Sweep`} size="small" color="success" sx={{ mt: 0.5, display: "block", width: "fit-content", fontSize: "0.7rem" }} />}
                              </TableCell>
                              <TableCell>
                                <Chip size="small" label={stats.incorrectAnswers} color={stats.incorrectAnswers > 0 ? "error" : "success"} sx={{ fontWeight: 700 }} />
                              </TableCell>
                              <TableCell>
                                <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>{swapeSessionsUsed}/{maxSwapes} used</Typography>
                                {swapeSessions.length > 0 && (
                                  <Stack spacing={0.5}>
                                    {swapeSessions.map((session, idx) => (
                                      <Button key={session.retake_token} size="small" variant="outlined" onClick={() => handleViewSwapeSession(session)} sx={{ textTransform: "none", fontSize: "0.72rem", borderRadius: 1.5, py: 0.2 }}>
                                        Sweep {idx + 1}: {session.percentage}%
                                      </Button>
                                    ))}
                                  </Stack>
                                )}
                              </TableCell>
                              <TableCell sx={{ fontSize: "0.8rem" }}>{attempt?.engine || "—"}</TableCell>
                              <TableCell>
                                <Stack direction="row" spacing={0.75} flexWrap="wrap">
                                  {showRetakeButton ? (
                                    <Button size="small" variant="contained" color="warning" onClick={() => handleSwapeClick(attempt)} disabled={retakeInProgress} sx={{ textTransform: "none", borderRadius: 2, fontWeight: 700 }}>
                                      {retakeInProgress ? "Starting…" : "Sweep"}
                                    </Button>
                                  ) : allSwapesExhausted ? (
                                    <Chip label={`${maxSwapes}/${maxSwapes}`} size="small" color="error" />
                                  ) : stats.incorrectAnswers === 0 ? (
                                    <Chip label="All correct" size="small" color="success" />
                                  ) : (
                                    isParent ? <Chip label="Parent view" size="small" /> : <Chip label="N/A" size="small" />
                                  )}
                                  <Button
                                    size="small"
                                    variant="contained"
                                    onClick={() => handleNavigateReview(selectedTestId, attempt.id)}
                                    sx={{ textTransform: "none", borderRadius: 2, fontWeight: 700, background: "linear-gradient(90deg, #667eea 0%, #764ba2 100%)", whiteSpace: "nowrap" }}
                                  >
                                    Review
                                  </Button>
                                  <Button size="small" variant="outlined" endIcon={<ArrowDropDownIcon />} onClick={(e) => handleOpenActionMenu(e, attempt.id)} sx={{ borderRadius: 2 }}>View</Button>
                                  <Menu anchorEl={actionMenuAnchor[attempt.id]} open={Boolean(actionMenuAnchor[attempt.id])} onClose={() => handleCloseActionMenu(attempt.id)}>
                                    {attempt?.answer_sheet_url && (
                                      <MenuItem component="a" href={attempt.answer_sheet_url} target="_blank" rel="noreferrer" onClick={() => handleCloseActionMenu(attempt.id)}>View Sheet</MenuItem>
                                    )}
                                    <MenuItem onClick={() => { fetchDetailedResult(attempt, false); handleCloseActionMenu(attempt.id); }}>View All Answers</MenuItem>
                                    <MenuItem onClick={() => { fetchDetailedResult(attempt, true); handleCloseActionMenu(attempt.id); }}>View Incorrect Only</MenuItem>
                                  </Menu>
                                </Stack>
                              </TableCell>
                            </TableRow>
                          );
                        });
                        })()}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
              </CardContent>
            </Collapse>
          </Card>
        </Box>
      )}

      {/* ── EVALUATION SUMMARY ─────────────────────────────────────────────── */}
      {tab === TAB_RESULTS && result && (
        <Box sx={{ mt: 4 }}>
          <Card sx={{ borderRadius: 3, mb: 3, boxShadow: "0 8px 32px rgba(26,35,126,0.12)" }}>
            <CardContent sx={{ cursor: "pointer", userSelect: "none" }} onClick={() => setShowEvaluationSummary((p) => !p)}>
              <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 1 }}>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography variant="h6" sx={{ fontWeight: 700 }}>Evaluation Summary</Typography>
                  {resultTestMeta && (resultTestMeta.subject || resultTestMeta.strand || resultTestMeta.subtopic) && (
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
                      {[resultTestMeta.subject, resultTestMeta.strand, resultTestMeta.subtopic].filter(Boolean).join(" › ")}
                    </Typography>
                  )}
                </Box>
                <Stack direction="row" alignItems="center" spacing={0.5} onClick={(e) => e.stopPropagation()}>
                  {result?.id && selectedTestId && (
                    <Button
                      size="small"
                      variant="contained"
                      onClick={() => handleNavigateReview(selectedTestId, result.id)}
                      sx={{ textTransform: "none", borderRadius: 2, fontWeight: 700, background: "linear-gradient(90deg, #667eea 0%, #764ba2 100%)", whiteSpace: "nowrap" }}
                    >
                      Full Review
                    </Button>
                  )}
                  {canVerifyWorksheet && result?.id && (
                    <Button
                      size="small"
                      variant={getWSSheetItemData("worksheet", result.id)?.my_verdict ? "contained" : "outlined"}
                      startIcon={<VerifiedUserIcon fontSize="small" />}
                      onClick={() => {
                        loadWSSheetVerifications("worksheet", result.id);
                        setWsSheetVerifyDialogOpen(true);
                      }}
                      sx={{
                        textTransform: "none",
                        fontWeight: 700,
                        borderRadius: 2,
                        color: getWSSheetItemData("worksheet", result.id)?.my_verdict ? "white" : "info.main",
                        borderColor: "info.main",
                        bgcolor: getWSSheetItemData("worksheet", result.id)?.my_verdict ? "info.main" : "transparent",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {getWSSheetItemData("worksheet", result.id)?.my_verdict
                        ? `Verified (${getWSSheetItemData("worksheet", result.id).my_verdict})`
                        : "Verify Sheet"}
                    </Button>
                  )}
                  <IconButton size="small" onClick={() => setShowEvaluationSummary((p) => !p)}>
                    <ExpandMoreIcon sx={{ transform: showEvaluationSummary ? "rotate(180deg)" : "none", transition: "transform 0.25s" }} />
                  </IconButton>
                  <IconButton size="small" aria-label="Close evaluation summary" onClick={() => { setResult(null); setResultTestMeta(null); setHideCorrectAnswers(!isParent); }}>
                    <CloseIcon />
                  </IconButton>
                </Stack>
              </Box>
            </CardContent>
            <Collapse in={showEvaluationSummary}>
              <Divider />
              <CardContent sx={{ pt: 2 }}>
                <Tabs
                  value={resultViewTab}
                  onChange={(_, v) => setResultViewTab(v)}
                  variant="scrollable"
                  scrollButtons="auto"
                  allowScrollButtonsMobile
                  sx={{ mb: 3, borderBottom: 1, borderColor: "divider" }}
                >
                  <Tab label="Score" value={RESULT_VIEW_SCORE} />
                  <Tab label="Subject / Topic" value={RESULT_VIEW_TOPIC} />
                  <Tab label="Question Analysis" value={RESULT_VIEW_QUESTIONS} />
                </Tabs>

                {/* Score tab */}
                {resultViewTab === RESULT_VIEW_SCORE && (
                  <Box>
                    {/* AI grading status indicator */}
                    {aiGradingStatus && aiGradingStatus !== "none" && aiGradingStatus !== "completed" && (
                      <Alert
                        severity={aiGradingStatus === "failed" || aiGradingStatus === "timeout" ? "warning" : "info"}
                        icon={aiGradingStatus === "pending" || aiGradingStatus === "processing" ? <AutorenewIcon sx={{ animation: "spin 1.5s linear infinite", "@keyframes spin": { "0%": { transform: "rotate(0deg)" }, "100%": { transform: "rotate(360deg)" } } }} /> : undefined}
                        sx={{ mb: 2 }}
                      >
                        {(aiGradingStatus === "pending" || aiGradingStatus === "processing")
                          ? "AI is verifying fill-in-the-blank and short answers… Score may update shortly."
                          : "AI verification could not complete. Showing local grading results."}
                      </Alert>
                    )}
                    {/* Big score */}
                    <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "auto 1fr" }, gap: 3, mb: 3, alignItems: "center" }}>
                      <Box
                        sx={{
                          width: 120, height: 120, borderRadius: "50%",
                          background: summaryPercentage >= 50 ? "linear-gradient(135deg, #d1fae5, #6ee7b7)" : "linear-gradient(135deg, #fee2e2, #fca5a5)",
                          display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
                          border: "3px solid", borderColor: summaryPercentage >= 50 ? "#10b981" : "#ef4444",
                          mx: { xs: "auto", sm: 0 },
                        }}
                      >
                        <Typography variant="h4" sx={{ fontWeight: 800, color: summaryPercentage >= 50 ? "#065f46" : "#991b1b", lineHeight: 1 }}>
                          {summaryPercentage}%
                        </Typography>
                        <Typography variant="caption" sx={{ color: summaryPercentage >= 50 ? "#047857" : "#b91c1c", fontWeight: 600 }}>Score</Typography>
                      </Box>
                      <Grid container spacing={2}>
                        {(() => {
                          const gradableMarks = result?.summary?.gradable_marks ?? null;
                          const totalMarks = result?.summary?.total_marks ?? null;
                          return [
                            { label: "Correct", value: correctAnswers, color: "#2e7d32", bg: "#e8f5e9", border: "#a5d6a7" },
                            { label: "Incorrect", value: incorrectAnswers, color: "#c62828", bg: "#ffebee", border: "#ef9a9a" },
                            gradableMarks != null
                              ? { label: "Gradable Marks", value: gradableMarks, color: "#374151", bg: "#f3f4f6", border: "#d1d5db" }
                              : { label: "Gradable", value: gradableTotal, color: "#374151", bg: "#f3f4f6", border: "#d1d5db" },
                            ...(totalMarks != null && totalMarks !== gradableMarks
                              ? [{ label: "Total Marks", value: totalMarks, color: "#374151", bg: "#f0fdf4", border: "#bbf7d0" }]
                              : []),
                          ];
                        })().map(({ label, value, color, bg, border }) => (
                          <Grid item xs={6} sm={4} md={3} key={label}>
                            <Box sx={{ textAlign: "center", p: 1.5, borderRadius: 2, bgcolor: bg, border: "1px solid", borderColor: border }}>
                              <Typography variant="h5" sx={{ fontWeight: 800, color }}>{value}</Typography>
                              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>{label}</Typography>
                            </Box>
                          </Grid>
                        ))}
                      </Grid>
                    </Box>
                    <Stack direction="row" spacing={1.5} flexWrap="wrap" gap={1}>
                      <Chip label={`Attempt ${result.attempt_count}`} color="primary" sx={{ fontWeight: 700 }} />
                      {result.engine && <Chip label={result.engine} size="small" color="secondary" sx={{ fontWeight: 600 }} />}
                      {result.answer_sheet_url && (
                        <Button href={result.answer_sheet_url} target="_blank" rel="noreferrer" size="small" variant="outlined" sx={{ borderRadius: 2 }}>
                          Open Submitted Sheet
                        </Button>
                      )}
                    </Stack>
                  </Box>
                )}

                {/* Topic tab */}
                {resultViewTab === RESULT_VIEW_TOPIC && resultTestMeta && (
                  <Box>
                    <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 2 }}>This Work Sheet Topic</Typography>
                    <Grid container spacing={2} sx={{ mb: 2 }}>
                      {[
                        { label: "Subject", value: resultTestMeta.subject },
                        { label: "Strand", value: resultTestMeta.strand },
                        { label: "Subtopic", value: resultTestMeta.subtopic },
                      ].map(({ label, value }) => (
                        <Grid item xs={12} sm={4} key={label}>
                          <Card variant="outlined" sx={{ p: 2, bgcolor: "#f8fafc", borderRadius: 2 }}>
                            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>{label}</Typography>
                            <Typography variant="body1" sx={{ fontWeight: 700, mt: 0.25 }}>{value || "—"}</Typography>
                          </Card>
                        </Grid>
                      ))}
                    </Grid>
                    <Typography variant="body2" color="text.secondary">
                      Score for this topic: <strong>{summaryPercentage}%</strong> ({correctAnswers}/{gradableTotal} correct)
                    </Typography>
                  </Box>
                )}
              </CardContent>
            </Collapse>
          </Card>

          {/* Question analysis (outside collapse so it scrolls nicely) */}
          {showEvaluationSummary && resultViewTab === RESULT_VIEW_QUESTIONS && (
            <Stack spacing={3}>
              {result.answers.length > 1 && (
                <Box sx={{ p: 2, bgcolor: "#f8f9fb", borderRadius: 2, border: "1px solid", borderColor: "divider" }}>
                  <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1.5, flexWrap: "wrap", gap: 1 }}>
                    <Typography variant="body2" sx={{ fontWeight: 700, color: "text.secondary" }}>Jump to Question</Typography>
                    <Box sx={{ display: "flex", gap: 1.5 }}>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                        <Box sx={{ width: 12, height: 12, borderRadius: 0.5, bgcolor: "#e8f5e9", border: "1.5px solid #4caf50" }} />
                        <Typography variant="caption" color="text.secondary">Correct</Typography>
                      </Box>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                        <Box sx={{ width: 12, height: 12, borderRadius: 0.5, bgcolor: "#ffebee", border: "1.5px solid #f44336" }} />
                        <Typography variant="caption" color="text.secondary">Incorrect</Typography>
                      </Box>
                    </Box>
                  </Box>
                  <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75, maxHeight: 200, overflow: "auto" }}>
                    {result.answers.map((answer, index) => {
                      const qPage = Math.ceil((index + 1) / EVALUATION_PAGE_SIZE);
                      const isCurrent = qPage === evaluationPage;
                      return (
                        <Button key={answer.question_id} size="small" variant={isCurrent ? "contained" : "outlined"} onClick={() => setEvaluationPage(qPage)}
                          sx={{ minWidth: 36, width: 36, height: 36, p: 0, fontSize: "0.78rem", borderRadius: 1.5, fontWeight: isCurrent ? 700 : 500,
                            borderColor: answer.is_correct === null ? "#ff9800" : answer.is_correct ? "#4caf50" : "#f44336",
                            color: isCurrent ? "white" : answer.is_correct === null ? "#e65100" : answer.is_correct ? "#2e7d32" : "#c62828",
                            bgcolor: isCurrent ? "primary.main" : answer.is_correct === null ? "#fff3e0" : answer.is_correct ? "#e8f5e9" : "#ffebee",
                            "&:hover": { opacity: 0.8 } }}>
                          {index + 1}
                        </Button>
                      );
                    })}
                  </Box>
                </Box>
              )}

              {paginatedEvaluationAnswers.items.map((answer) => {
                const shouldHideCorrect = !isParent && hideCorrectAnswers && !answer.is_correct;
                const showExplanation = isParent || answer.is_correct;
                return (
                  <Card key={answer.question_id} variant="outlined" sx={{ borderRadius: 2, border: "2px solid", borderColor: answer.is_correct === null ? "#ff9800" : answer.is_correct ? "#4caf50" : "#f44336" }}>
                    <CardContent>
                      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }} flexWrap="wrap" gap={1}>
                        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                          <Chip label={`Q ${answer.displayNumber} of ${result.answers.length}`} color="primary" size="small" sx={{ fontWeight: 700 }} />
                          {(answer.subject || answer.strand || answer.subtopic) && (
                            <Chip label={[answer.subject, answer.strand, answer.subtopic].filter(Boolean).join(" › ")} size="small" variant="outlined"
                              sx={{ maxWidth: 260, "& .MuiChip-label": { overflow: "hidden", textOverflow: "ellipsis" } }} />
                          )}
                          {answer.answer_type && answer.answer_type !== "mcq" && (
                            <Chip label={answer.answer_type.replace("_", " ")} size="small" variant="outlined" color="info" sx={{ textTransform: "capitalize" }} />
                          )}
                          {answer.marks != null && (
                            <Chip
                              label={answer.answer_type === "vocab_matching"
                                ? `${answer.pairs_total ?? "?"} pairs = ${answer.pairs_total ?? "?"} marks`
                                : `${answer.marks} mark${answer.marks !== 1 ? "s" : ""}`}
                              size="small"
                              sx={{ fontWeight: 600, bgcolor: "#f0fdf4", color: "#166534", border: "1px solid #bbf7d0" }}
                            />
                          )}
                        </Stack>
                        <Chip
                          label={answer.is_correct === null ? (answer.attempted ? "Submitted" : "Not Attempted") : answer.is_correct ? "Correct" : "Incorrect"}
                          color={answer.is_correct === null ? "warning" : answer.is_correct ? "success" : "error"}
                          icon={answer.is_correct === null ? undefined : answer.is_correct ? <CheckCircleIcon /> : <CancelIcon />}
                          sx={{ fontWeight: 700 }}
                        />
                      </Stack>

                      <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>{answer.question_text || "—"}</Typography>
                      {renderQuestionVisual(answer, `eval-${answer.question_id}`)}

                      {/* MCQ options display */}
                      {(!answer.answer_type || answer.answer_type === "mcq") && answer.options && answer.options.length > 0 && (
                        <Box sx={{ mb: 2 }}>
                          <Typography variant="body2" sx={{ fontWeight: 700, mb: 1 }}>Options</Typography>
                          <Stack spacing={1}>
                            {answer.options.map((option) => {
                              const isSelected = answer.selected_letter === option.letter;
                              const isCorrect = answer.correct_letter === option.letter;
                              const isSelectedCorrect = isSelected && answer.is_correct;
                              const isSelectedIncorrect = isSelected && !answer.is_correct;
                              let borderColor = "#e0e0e0", backgroundColor = "transparent", icon = null;
                              if (isSelectedCorrect) { borderColor = "#4caf50"; backgroundColor = "#e8f5e9"; icon = <CheckCircleIcon sx={{ color: "#4caf50", fontSize: 20 }} />; }
                              else if (isSelectedIncorrect) { borderColor = "#f44336"; backgroundColor = "#ffebee"; icon = <CancelIcon sx={{ color: "#f44336", fontSize: 20 }} />; }
                              else if (isCorrect && !shouldHideCorrect) { borderColor = "#81c784"; backgroundColor = "#f1f8f4"; }
                              return (
                                <Box key={option.option_id} sx={{ p: 1.5, borderRadius: 1.5, border: `2px solid ${borderColor}`, backgroundColor, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                                  <Typography><strong>{option.letter}.</strong> {option.text}</Typography>
                                  {icon}
                                </Box>
                              );
                            })}
                          </Stack>
                        </Box>
                      )}

                      {/* Non-MCQ text answer display */}
                      {answer.answer_type === "vocab_matching" && answer.matched_pairs ? (
                        <Box sx={{ mb: 2 }}>
                          <Stack direction="row" alignItems="center" gap={1} sx={{ mb: 1 }}>
                            <Typography variant="body2" sx={{ fontWeight: 700 }}>Vocabulary Matching</Typography>
                            <Chip size="small" label={`${answer.pairs_correct ?? 0}/${answer.pairs_total ?? 0} pairs`}
                              color={answer.pairs_correct === answer.pairs_total ? "success" : answer.pairs_correct > 0 ? "warning" : "error"} sx={{ fontWeight: 700 }} />
                            <Chip size="small" label={`${answer.pairs_correct ?? 0} / ${answer.pairs_total ?? 0} marks`}
                              variant="outlined" sx={{ fontWeight: 600, borderColor: "#bbf7d0", color: "#166534" }} />
                          </Stack>
                          <Box sx={{ display: "grid", gridTemplateColumns: "auto 1fr auto 1fr auto", gap: 0.5, alignItems: "center" }}>
                            {answer.matched_pairs.map((p, pi) => (
                              <Box key={pi} sx={{ display: "contents" }}>
                                <Typography variant="body2" sx={{ fontWeight: 600, pr: 0.5, textAlign: "right", color: "#374151" }}>{p.word_number}.</Typography>
                                <Typography variant="body2" noWrap title={p.word_text}>{p.word_text || `Word ${p.word_number}`}</Typography>
                                <Typography variant="body2" sx={{ textAlign: "center", px: 0.5, fontWeight: 700 }}>→ {p.matched_letter || "?"}</Typography>
                                <Typography variant="body2" noWrap title={p.def_text} sx={{ color: "#374151" }}>{p.def_text || "—"}</Typography>
                                {p.is_correct
                                  ? <CheckCircleIcon sx={{ fontSize: 18, color: "#4caf50" }} />
                                  : <CancelIcon sx={{ fontSize: 18, color: "#f44336" }} />}
                              </Box>
                            ))}
                          </Box>
                          {answer.extraction_confidence && (
                            <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: "block" }}>
                              Extraction confidence: {answer.extraction_confidence}
                            </Typography>
                          )}
                        </Box>
                      ) : answer.answer_type && answer.answer_type !== "mcq" ? (
                        <Grid container spacing={2} sx={{ mb: 2 }}>
                          {answer.answer_type === "writing" && (() => {
                            const minWords = answer.ai_metadata?.min_words ?? answer.writing_min_words ?? 75;
                            const maxWords = answer.ai_metadata?.max_words ?? 100;
                            const wCount = answer.writing_word_count ?? (answer.text_answer?.trim() ? answer.text_answer.trim().split(/\s+/).length : 0);
                            const wMet = wCount >= minWords;
                            return (
                              <Grid item xs={12}>
                                <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
                                  <Typography variant="body2" color="text.secondary">
                                    Writing: {minWords}–{maxWords} words required for full marks
                                  </Typography>
                                  {wCount > 0 ? (
                                    <Chip
                                      label={wMet ? `${wCount} words ✓` : `${wCount} words — needs ${minWords - wCount} more`}
                                      size="small"
                                      color={wMet ? "success" : "error"}
                                      sx={{ fontWeight: 700 }}
                                    />
                                  ) : (
                                    <Chip label="No text extracted" size="small" color="default" />
                                  )}
                                </Stack>
                              </Grid>
                            );
                          })()}
                          <Grid item xs={12}>
                            <Typography variant="body2" sx={{ fontWeight: 700, mb: 0.5 }}>Your Answer:</Typography>
                            <Box sx={{ p: 1.5, borderRadius: 1.5, border: "1px solid #e0e0e0", bgcolor: "#f9fafb", whiteSpace: "pre-line" }}>
                              <Typography variant="body2">{answer.text_answer || "—"}</Typography>
                            </Box>
                            {answer.extraction_confidence && (
                              <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: "block" }}>
                                Extraction confidence: {answer.extraction_confidence}
                              </Typography>
                            )}
                          </Grid>
                          {answer.correct_text && !shouldHideCorrect && (
                            <Grid item xs={12}>
                              <Typography variant="body2" sx={{ fontWeight: 700, mb: 0.5 }}>Correct Answer:</Typography>
                              <Box sx={{ p: 1.5, borderRadius: 1.5, border: "1px solid #81c784", bgcolor: "#f1f8f4" }}>
                                <Typography variant="body2">{answer.correct_text}</Typography>
                              </Box>
                            </Grid>
                          )}
                        </Grid>
                      ) : (
                        <Grid container spacing={2} sx={{ mb: 2 }}>
                          <Grid item xs={12} sm={6}>
                            <Typography variant="body2" color="text.secondary">Your Answer: <Typography component="span" sx={{ fontWeight: 700, color: "text.primary" }}>{answer.selected_letter || "—"}</Typography></Typography>
                          </Grid>
                          <Grid item xs={12} sm={6}>
                            <Typography variant="body2" color="text.secondary">Correct Answer:{" "}
                              {shouldHideCorrect ? <Typography component="span" fontStyle="italic" color="text.secondary">Hidden</Typography>
                                : <Typography component="span" sx={{ fontWeight: 700, color: "text.primary" }}>{answer.correct_letter || "—"}</Typography>}
                            </Typography>
                          </Grid>
                        </Grid>
                      )}

                      {showExplanation && !shouldHideCorrect && (answer.explanation || answer.explanation_video_url) && (
                        <Box>
                          <Divider sx={{ my: 2 }} />
                          <Typography variant="body2" sx={{ fontWeight: 700, mb: 1 }}>Explanation</Typography>
                          {answer.explanation && (
                            <Box sx={{ color: "text.secondary", fontSize: "0.875rem", lineHeight: 1.43, mb: 1, "& p": { m: 0 } }}>
                              <HtmlWithLatex html={answer.explanation} />
                            </Box>
                          )}
                          {answer.explanation_video_url && (
                            <Box sx={{ mt: 1 }}>
                              <VideoExplanationPlayer videoUrl={makeAbsoluteUrl(answer.explanation_video_url)} questionText={answer.question_text} variant="button" />
                            </Box>
                          )}
                        </Box>
                      )}
                      {showExplanation && !shouldHideCorrect && !answer.explanation && !answer.explanation_video_url && (
                        <Box><Divider sx={{ my: 2 }} /><Typography variant="body2" color="text.secondary" fontStyle="italic">Explanation pending</Typography></Box>
                      )}
                    </CardContent>
                  </Card>
                );
              })}

              {result.answers.length > EVALUATION_PAGE_SIZE && (
                <Stack direction="row" spacing={2} alignItems="center" justifyContent="space-between">
                  <Typography variant="body2" color="text.secondary">Page {Math.min(evaluationPage, paginatedEvaluationAnswers.totalPages)} of {paginatedEvaluationAnswers.totalPages}</Typography>
                  <Stack direction="row" spacing={1}>
                    <Button size="small" variant="outlined" onClick={() => setEvaluationPage((p) => Math.max(p - 1, 1))} disabled={evaluationPage <= 1} sx={{ borderRadius: 2 }}>Previous</Button>
                    <Button size="small" variant="contained" onClick={() => setEvaluationPage((p) => Math.min(p + 1, paginatedEvaluationAnswers.totalPages))} disabled={evaluationPage >= paginatedEvaluationAnswers.totalPages} sx={{ borderRadius: 2 }}>Next</Button>
                  </Stack>
                </Stack>
              )}
            </Stack>
          )}
        </Box>
      )}

      {/* ── SWEEP SESSION ──────────────────────────────────────────────────── */}
      {retakeSession && (
        <Box sx={{ mt: 4 }}>
          <Card sx={{ borderRadius: 3, mb: 3, boxShadow: "0 4px 20px rgba(0,0,0,0.08)" }}>
            <CardContent>
              {/* Sweep header */}
              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2, flexWrap: "wrap", gap: 1.5 }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                  <Box sx={{ width: 4, height: 24, borderRadius: 1, background: "linear-gradient(180deg, #667eea, #764ba2)" }} />
                  <Typography variant="h6" sx={{ fontWeight: 700 }}>Sweep</Typography>
                  {retakeSession?.questions?.length > 0 && swapeTotalSeconds > 0 && (
                    <Chip
                      icon={<AccessTimeIcon sx={{ fontSize: 15 }} />}
                      label={swapeTotalRemaining > 0 ? formatSeconds(swapeTotalRemaining) : "Time up"}
                      color={swapeTotalRemaining === 0 ? "error" : swapeTotalRemaining <= 30 ? "error" : swapeTotalRemaining <= 120 ? "warning" : "default"}
                      size="small"
                      sx={{ fontWeight: 700 }}
                    />
                  )}
                </Box>
                <Stack direction="row" spacing={1}>
                  <Button size="small" variant="outlined" onClick={handleClearRetake} sx={{ borderRadius: 2 }}>Close</Button>
                  <Button size="small" variant="contained" onClick={handleSubmitRetake} disabled={submittingRetake || (swapeTotalSeconds > 0 && swapeTotalRemaining === 0)}
                    sx={{ borderRadius: 2, fontWeight: 700, background: "linear-gradient(90deg, #667eea 0%, #764ba2 100%)" }}>
                    {submittingRetake ? "Submitting…" : "Submit Sweep"}
                  </Button>
                </Stack>
              </Box>

              <Alert severity="info" sx={{ mb: 2, borderRadius: 2 }}>
                Sweep Session {(activeSwapeMeta?.swapeSessionsUsed ?? retakeHistory?.latest_swape_sessions_used ?? 0) + 1} of{" "}
                {activeSwapeMeta?.maxSwapesPerUpload ?? retakeHistory?.max_swapes_per_upload ?? 4} for this upload
                {swapeTotalSeconds > 0 && swapeTotalRemaining === 0 && (
                  <Typography variant="caption" sx={{ display: "block", mt: 0.5 }}>Time has expired. This session counts as used.</Typography>
                )}
              </Alert>

              {/* Question navigator */}
              {retakeSession?.questions?.length > 1 && (
                <Box sx={{ mb: 3, p: 2, bgcolor: "#f8f9fb", borderRadius: 2, border: "1px solid", borderColor: "divider" }}>
                  <Typography variant="body2" sx={{ fontWeight: 700, color: "text.secondary", mb: 1.5 }}>Jump to Question</Typography>
                  <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75, maxHeight: 200, overflow: "auto" }}>
                    {retakeSession.questions.map((q, index) => {
                      const qPage = Math.ceil((index + 1) / RETAKE_PAGE_SIZE);
                      const isCurrent = qPage === retakePage;
                      const hasAnswer = !!retakeAnswers[q.question_id];
                      return (
                        <Button key={q.question_id} size="small" variant={isCurrent ? "contained" : "outlined"} onClick={() => setRetakePage(qPage)}
                          sx={{ minWidth: 36, width: 36, height: 36, p: 0, fontSize: "0.78rem", borderRadius: 1.5, fontWeight: isCurrent ? 700 : 500,
                            borderColor: hasAnswer ? "#4caf50" : "#9e9e9e",
                            color: isCurrent ? "white" : hasAnswer ? "#2e7d32" : "#374151",
                            bgcolor: isCurrent ? "primary.main" : hasAnswer ? "#e8f5e9" : "transparent",
                            "&:hover": { opacity: 0.8 } }}>
                          {index + 1}
                        </Button>
                      );
                    })}
                  </Box>
                </Box>
              )}

              {/* Sweep questions */}
              <Stack spacing={2}>
                {paginatedRetakeQuestions.items.map((question) => {
                  const isGlobalExpired = swapeTotalSeconds > 0 && swapeTotalRemaining === 0;
                  const selectedLetter = retakeAnswers[question.question_id] || "";
                  return (
                    <Card key={question.question_id} variant="outlined" sx={{ borderRadius: 2 }}>
                      <CardContent>
                        <Stack direction={{ xs: "column", sm: "row" }} spacing={1} alignItems={{ xs: "flex-start", sm: "center" }} justifyContent="space-between" sx={{ mb: 1.5 }}>
                          <Chip label={`Question ${question.displayNumber ?? question.sequence ?? "—"}`} color="primary" size="small" sx={{ fontWeight: 700 }} />
                          <Button size="small" variant={flaggedQuestions.has(question.question_id) ? "contained" : "outlined"} color="warning"
                            startIcon={flaggingQuestion[question.question_id] ? <CircularProgress size={14} color="inherit" /> : <FlagIcon />}
                            onClick={() => handleFlagQuestion(question.question_id)}
                            disabled={isGlobalExpired || flaggingQuestion[question.question_id]}
                            sx={{ textTransform: "none", borderRadius: 2, fontWeight: 600 }}>
                            {flaggingQuestion[question.question_id] ? (flaggedQuestions.has(question.question_id) ? "Unflagging…" : "Flagging…") : flaggedQuestions.has(question.question_id) ? "Unflag" : "Flag"}
                          </Button>
                        </Stack>

                        <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1.5 }}>{question.question_text || "—"}</Typography>
                        {renderQuestionVisual(question, `sweep-${question.question_id}`)}

                        <Typography variant="body2" sx={{ fontWeight: 700, mb: 1 }}>Options</Typography>
                        <Stack spacing={1}>
                          {question.options?.map((option) => {
                            const isSelected = selectedLetter === option.letter;
                            return (
                              <Button key={option.option_id}
                                variant={isSelected ? "contained" : "outlined"}
                                color={isSelected ? "success" : "primary"}
                                onClick={() => handleRetakeAnswerChange(question.question_id, option.letter)}
                                disabled={isGlobalExpired}
                                sx={{ justifyContent: "flex-start", textTransform: "none", borderRadius: 2, py: 1.2 }}>
                                <Typography sx={{ fontWeight: 700, mr: 1 }}>{option.letter}.</Typography>
                                <Typography>{option.text || "Option"}</Typography>
                              </Button>
                            );
                          })}
                        </Stack>
                        <Divider sx={{ my: 2 }} />
                        <Typography variant="body2" color="text.secondary">
                          Selected: <Typography component="span" sx={{ fontWeight: 700, color: "text.primary" }}>{selectedLetter || "—"}</Typography>
                        </Typography>
                      </CardContent>
                    </Card>
                  );
                })}
              </Stack>

              {retakeSession.questions.length > RETAKE_PAGE_SIZE && (
                <Stack direction="row" spacing={2} alignItems="center" justifyContent="space-between" sx={{ mt: 3 }}>
                  <Typography variant="body2" color="text.secondary">Page {Math.min(retakePage, paginatedRetakeQuestions.totalPages)} of {paginatedRetakeQuestions.totalPages}</Typography>
                  <Stack direction="row" spacing={1}>
                    <Button size="small" variant="outlined" onClick={() => setRetakePage((p) => Math.max(p - 1, 1))} disabled={retakePage <= 1} sx={{ borderRadius: 2 }}>Previous</Button>
                    <Button size="small" variant="contained" onClick={() => setRetakePage((p) => Math.min(p + 1, paginatedRetakeQuestions.totalPages))} disabled={retakePage >= paginatedRetakeQuestions.totalPages} sx={{ borderRadius: 2 }}>Next</Button>
                  </Stack>
                </Stack>
              )}
            </CardContent>
          </Card>

          {/* Sweep result */}
          {retakeResult && (
            <Box>
              <Card sx={{ borderRadius: 3, mb: 3, boxShadow: "0 4px 20px rgba(0,0,0,0.08)" }}>
                <CardContent>
                  <Typography variant="h6" sx={{ fontWeight: 700, mb: 2.5 }}>Sweep Summary</Typography>
                  <Grid container spacing={2} sx={{ mb: 2 }}>
                    {[
                      { label: "Score", value: `${retakeResult?.summary?.percentage ?? 0}%`, color: "#1a237e", bg: "#e8eaf6" },
                      { label: "Correct", value: retakeResult?.summary?.correct_answers ?? 0, color: "#2e7d32", bg: "#e8f5e9" },
                      { label: "Total", value: retakeResult?.summary?.gradable_total ?? retakeResult?.summary?.total_questions ?? 0, color: "#374151", bg: "#f3f4f6" },
                    ].map(({ label, value, color, bg }) => (
                      <Grid item xs={6} sm={4} md={3} key={label}>
                        <Box sx={{ textAlign: "center", p: 2, borderRadius: 2, bgcolor: bg }}>
                          <Typography variant="h4" sx={{ fontWeight: 800, color }}>{value}</Typography>
                          <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>{label}</Typography>
                        </Box>
                      </Grid>
                    ))}
                  </Grid>

                  {retakeResult?.answers && retakeResult.answers.length > 0 && (
                    <TableContainer component={Paper} sx={{ borderRadius: 2, overflow: "auto", maxHeight: 420 }} variant="outlined">
                      <Table size="small" stickyHeader>
                        <TableHead>
                          <TableRow>
                            {["#", "Question", "Selected", "Correct", "Status", "AI Explanation"].map((h) => (
                              <TableCell key={h} sx={{ fontWeight: 700, bgcolor: "#f8f9fb" }}>{h}</TableCell>
                            ))}
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {retakeResult.answers.map((answer, idx) => (
                            <TableRow key={answer.question_id || idx} sx={{ "&:hover": { bgcolor: "#f8f9fb" } }}>
                              <TableCell>{answer.question_number || idx + 1}</TableCell>
                              <TableCell sx={{ maxWidth: 320 }}>
                                <Typography variant="body2" noWrap title={answer.question_text}>{answer.question_text || "—"}</Typography>
                              </TableCell>
                              <TableCell>
                                {answer.answer_type === "vocab_matching" && answer.matched_pairs
                                  ? <Chip size="small" label={`${answer.pairs_correct ?? 0}/${answer.pairs_total ?? 0} pairs`} color={answer.pairs_correct === answer.pairs_total ? "success" : "warning"} />
                                  : answer.answer_type && answer.answer_type !== "mcq"
                                    ? <Typography variant="body2" noWrap title={answer.text_answer} sx={{ maxWidth: 200 }}>{answer.text_answer || "—"}</Typography>
                                    : (answer.selected_letter ? `${answer.selected_letter}${answer.selected_option ? ` (${answer.selected_option})` : ""}` : "—")
                                }
                              </TableCell>
                              <TableCell>
                                {answer.answer_type === "vocab_matching" && answer.matched_pairs
                                  ? <Typography variant="body2">{answer.pairs_total ?? 0} pairs</Typography>
                                  : answer.answer_type && answer.answer_type !== "mcq"
                                    ? <Typography variant="body2" noWrap title={answer.correct_text} sx={{ maxWidth: 200 }}>{answer.correct_text || "—"}</Typography>
                                    : (answer.correct_letter ? `${answer.correct_letter}${answer.correct_option ? ` (${answer.correct_option})` : ""}` : "—")
                                }
                              </TableCell>
                              <TableCell>
                                <Chip size="small"
                                  label={answer.is_correct === null ? (answer.attempted ? "Submitted" : "—") : answer.is_correct ? "Correct" : "Incorrect"}
                                  color={answer.is_correct === null ? "warning" : answer.is_correct ? "success" : "error"}
                                  sx={{ fontWeight: 700 }}
                                />
                              </TableCell>
                              <TableCell sx={{ minWidth: 300, maxWidth: 460 }}>
                                {answer.explanation || answer.explanation_video_url ? (
                                  <Box>
                                    {answer.explanation && (
                                      <Box sx={{ color: "text.secondary", fontSize: "0.875rem", lineHeight: 1.43, "& p": { m: 0 } }}>
                                        <HtmlWithLatex html={answer.explanation} />
                                      </Box>
                                    )}
                                    {answer.explanation_video_url && <Box sx={{ mt: 1 }}><VideoExplanationPlayer videoUrl={makeAbsoluteUrl(answer.explanation_video_url)} questionText={answer.question_text} variant="button" /></Box>}
                                  </Box>
                                ) : (
                                  <Typography variant="body2" color="text.secondary" fontStyle="italic">Explanation pending…</Typography>
                                )}
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  )}
                </CardContent>
              </Card>
            </Box>
          )}
        </Box>
      )}

      {/* ── UPLOAD DIALOG ─────────────────────────────────────────────────── */}
      <Dialog open={uploadDialogOpen} onClose={handleCloseUploadDialog} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>Upload Answer Sheet</DialogTitle>
        <DialogContent>
          <Alert severity="info" sx={{ mb: 2, borderRadius: 2 }}>
            Use the exported answer sheet. Keep corner fiducials visible for best accuracy.
          </Alert>
          <Box
            component="label"
            htmlFor="answer-sheet-file-input"
            onDragEnter={handleDropZoneDragEnter}
            onDragLeave={handleDropZoneDragLeave}
            onDragOver={handleDropZoneDragOver}
            onDrop={handleDropZoneDrop}
            sx={{
              display: "block",
              mb: 2,
              borderRadius: 2,
              border: "2px dashed",
              borderColor: dropZoneActive ? "primary.main" : "divider",
              bgcolor: dropZoneActive ? "action.hover" : "background.paper",
              py: 3,
              px: 2,
              cursor: "pointer",
              textAlign: "center",
              transition: "border-color 0.2s ease, background-color 0.2s ease",
              "&:focus-within": {
                borderColor: "primary.light",
                bgcolor: "action.hover",
              },
            }}
          >
            <input
              id="answer-sheet-file-input"
              type="file"
              hidden
              accept=".pdf,.png,.jpg,.jpeg"
              onChange={handleFileChange}
            />
            <Stack alignItems="center" spacing={1}>
              <CloudUploadIcon color="primary" sx={{ fontSize: 40 }} />
              <Typography variant="body2" sx={{ fontWeight: file ? 600 : 500, color: "text.primary", wordBreak: "break-word" }}>
                {file ? file.name : "Drag & drop a file here, or click to browse"}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                PDF, PNG, or JPG — same as choosing &quot;Attach&quot; before
              </Typography>
            </Stack>
          </Box>
          {validating && (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mt: 2, p: 1.5, bgcolor: "rgba(102,126,234,0.07)", borderRadius: 2 }}>
              <CircularProgress size={18} />
              <Typography variant="body2" color="primary" sx={{ fontWeight: 600 }}>
                Checking file matches selected worksheet…
              </Typography>
            </Box>
          )}
          {validationOk && (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mt: 2, p: 1.5, bgcolor: "rgba(46,125,50,0.08)", border: "1px solid rgba(46,125,50,0.3)", borderRadius: 2 }}>
              <CheckCircleIcon sx={{ color: "success.main", fontSize: 20 }} />
              <Typography variant="body2" sx={{ fontWeight: 700, color: "success.dark" }}>
                Answer sheet verified — correct worksheet (QR confirmed)
              </Typography>
            </Box>
          )}
          {(uploading || processingStage) && processingStage !== "complete" && (
            <Box sx={{ mt: 2 }}>
              <Stepper activeStep={UPLOAD_STAGES.findIndex((s) => s.key === processingStage)} alternativeLabel>
                {UPLOAD_STAGES.map((s) => <Step key={s.key}><StepLabel>{s.label}</StepLabel></Step>)}
              </Stepper>
              {processingStage === "uploading" && (
                <LinearProgress variant="determinate" value={uploadProgress} sx={{ mt: 2, height: 8, borderRadius: 4 }} />
              )}
              {processingStage !== "uploading" && processingStage !== "complete" && (
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 2, flexWrap: "wrap" }}>
                  {processingStage === "explanations" && pollingPaused ? (
                    <PauseIcon color="warning" sx={{ fontSize: 20 }} />
                  ) : (
                    <CircularProgress size={20} />
                  )}
                  <Typography variant="body2" sx={{ flex: 1 }}>
                    {processingStage === "explanations" && pollingPaused
                      ? "Monitoring paused — explanations still generating in background"
                      : `${UPLOAD_STAGES.find((s) => s.key === processingStage)?.label || "Processing"}…`}
                  </Typography>
                  {processingStage === "explanations" && (
                    pollingPaused ? (
                      <Button
                        size="small"
                        variant="contained"
                        color="success"
                        startIcon={<PlayCircleOutlineIcon />}
                        onClick={handleResumePolling}
                        sx={{ borderRadius: 1.5, fontWeight: 600 }}
                      >
                        Resume
                      </Button>
                    ) : (
                      <Button
                        size="small"
                        variant="outlined"
                        color="warning"
                        startIcon={<PauseIcon />}
                        onClick={handlePausePolling}
                        sx={{ borderRadius: 1.5, fontWeight: 600 }}
                      >
                        Pause
                      </Button>
                    )
                  )}
                </Box>
              )}
            </Box>
          )}
          {processingStage === "complete" && !uploading && (
            <Alert severity="success" sx={{ mt: 2, borderRadius: 2 }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <CheckCircleIcon color="success" /> Answer sheet processed successfully!
              </Box>
            </Alert>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
          <Button onClick={handleCloseUploadDialog} sx={{ borderRadius: 2 }}>Close</Button>
          <Button variant="contained" startIcon={validating ? <CircularProgress size={16} color="inherit" /> : <AssignmentTurnedInIcon />} disabled={disableSubmit}
            sx={{ borderRadius: 2, fontWeight: 700 }}
            onClick={async () => {
              const canProceed = await validateBeforeUpload(true);
              if (!canProceed) return; // validation warning dialog will handle the rest
              const data = await handleUpload({ navigateToReview: true });
              if (!data?.id || !selectedTestId) return;
              handleCloseUploadDialog();
              navigate(`/${prefix}/work-sheet/${selectedTestId}/attempt/${data.id}/review`, {
                state: isParent && selectedStudentId ? { studentId: selectedStudentId } : undefined,
              });
              fetchAttemptHistory(selectedTestId, isParent ? selectedStudentId : undefined);
              fetchRetakeHistory(selectedTestId, isParent ? selectedStudentId : undefined);
            }}>
            {validating ? "Checking…" : uploading ? "Processing…" : "Submit"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── VALIDATION WARNING DIALOG ─────────────────────────────────────── */}
      <Dialog open={!!validationWarning} onClose={() => setValidationWarning(null)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 700, display: "flex", alignItems: "center", gap: 1 }}>
          {validationWarning?.warnOnly
            ? <><AutoAwesomeIcon color="info" /> Answer Sheet Note</>
            : <><CancelIcon color={validationWarning?.method === "qr" || validationWarning?.method === "upload_error" ? "error" : "warning"} />
              {validationWarning?.method === "qr" || validationWarning?.method === "upload_error"
                ? "Wrong Answer Sheet Detected"
                : validationWarning?.method === "ocr_text" ? "Test ID Mismatch" : "Answer Sheet Mismatch"}</>
          }
        </DialogTitle>
        <DialogContent>
          {/* QR-confirmed or server-confirmed wrong sheet — strong error */}
          {!validationWarning?.warnOnly && (validationWarning?.method === "qr" || validationWarning?.method === "upload_error") && (
            <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>
              {validationWarning?.method === "upload_error"
                ? <>The uploaded answer sheet <strong>does not match</strong> the selected worksheet. Please upload the correct answer sheet for this test.</>
                : <>The QR code on the uploaded document confirms it belongs to a <strong>different worksheet</strong>. Uploading this sheet will give incorrect results.</>
              }
            </Alert>
          )}
          {/* AI visual / OCR check mismatch — softer warning */}
          {!validationWarning?.warnOnly && validationWarning?.method !== "qr" && validationWarning?.method !== "upload_error" && (
            <Alert severity="warning" sx={{ mb: 2, borderRadius: 2 }}>
              The uploaded file does not appear to match the selected worksheet.
              Please check you have the correct answer sheet before submitting.
            </Alert>
          )}
          {/* Valid but has non-critical issues (e.g. question count mismatch) */}
          {validationWarning?.warnOnly && (
            <Alert severity="info" sx={{ mb: 2, borderRadius: 2 }}>
              The answer sheet was verified but there is a version mismatch. You can still upload — review the note below.
            </Alert>
          )}

          {validationWarning?.testName && (
            <Typography variant="body2" sx={{ mb: 1.5, fontWeight: 600 }}>
              Selected worksheet: <em>{validationWarning.testName}</em>
            </Typography>
          )}

          {validationWarning?.issues?.length > 0 && (
            <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
              {validationWarning.issues.map((issue, i) => (
                <Typography key={i} component="li" variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                  {issue}
                </Typography>
              ))}
            </Box>
          )}

          {/* Method badge */}
          <Box sx={{ mt: 2, display: "flex", alignItems: "center", gap: 1 }}>
            <Chip
              size="small"
              icon={
                validationWarning?.method === "qr"
                  ? <FactCheckIcon sx={{ fontSize: 13 }} />
                  : validationWarning?.method === "ocr_text"
                    ? <FactCheckIcon sx={{ fontSize: 13 }} />
                    : validationWarning?.method === "upload_error"
                      ? <CancelIcon sx={{ fontSize: 13 }} />
                      : <AutoAwesomeIcon sx={{ fontSize: 13 }} />
              }
              label={
                validationWarning?.method === "qr"
                  ? "Verified by QR code"
                  : validationWarning?.method === "ocr_text"
                    ? "Read from header text"
                    : validationWarning?.method === "upload_error"
                      ? "Detected during processing"
                      : "Checked by AI"
              }
              variant="outlined"
              color={validationWarning?.method === "qr" || validationWarning?.method === "ocr_text" || validationWarning?.method === "upload_error" ? "error" : "warning"}
              sx={{ fontSize: "0.7rem" }}
            />
          </Box>

          {!validationWarning?.warnOnly && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5 }}>
              {validationWarning?.method === "upload_error"
                ? "Please select the correct answer sheet file and try again."
                : validationWarning?.method === "qr" || validationWarning?.method === "ocr_text"
                  ? "If you are certain this is the correct sheet, you may upload anyway — but results will likely be inaccurate."
                  : "You can upload anyway if you are sure this is the correct file."}
            </Typography>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
          <Button onClick={() => { setValidationWarning(null); if (validationWarning?.method === "upload_error") setFile(null); }} sx={{ borderRadius: 2 }}>
            {validationWarning?.method === "upload_error"
              ? "Choose Different File"
              : validationWarning?.warnOnly ? "Cancel" : "Cancel — Choose Different File"}
          </Button>
          {validationWarning?.method !== "upload_error" && (
            <Button
              variant="contained"
              color={validationWarning?.warnOnly ? "primary" : "warning"}
              startIcon={<AssignmentTurnedInIcon />}
              sx={{ borderRadius: 2, fontWeight: 700 }}
              onClick={async () => {
                const navigateToReview = validationWarning?.navigateToReview ?? true;
                setValidationWarning(null);
                const data = await handleUpload({ navigateToReview });
                if (!navigateToReview || !data?.id || !selectedTestId) return;
                handleCloseUploadDialog();
                navigate(`/${prefix}/work-sheet/${selectedTestId}/attempt/${data.id}/review`, {
                  state: isParent && selectedStudentId ? { studentId: selectedStudentId } : undefined,
                });
                fetchAttemptHistory(selectedTestId, isParent ? selectedStudentId : undefined);
                fetchRetakeHistory(selectedTestId, isParent ? selectedStudentId : undefined);
              }}
            >
              {validationWarning?.warnOnly ? "Upload Anyway" : "Upload Anyway"}
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* ── SWEEP SESSION MODAL ────────────────────────────────────────────── */}
      <Dialog open={swapeSessionModalOpen} onClose={handleCloseSwapeModal} maxWidth="md" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>
          Sweep Session Summary
          <IconButton onClick={handleCloseSwapeModal} sx={{ position: "absolute", right: 8, top: 8 }}><CloseIcon /></IconButton>
        </DialogTitle>
        <DialogContent>
          {selectedSwapeSession && (
            <>
              <Grid container spacing={2} sx={{ mb: 3, mt: 1 }}>
                {[
                  { label: "Score", value: `${selectedSwapeSession.percentage}%`, color: "#1a237e", bg: "#e8eaf6" },
                  { label: "Correct", value: selectedSwapeSession.correct_answers, color: "#2e7d32", bg: "#e8f5e9" },
                  { label: "Incorrect", value: selectedSwapeSession.incorrect_answers, color: "#d84315", bg: "#fbe9e7" },
                ].map(({ label, value, color, bg }) => (
                  <Grid item xs={6} sm={4} md={3} key={label}>
                    <Paper sx={{ p: 2, textAlign: "center", background: bg, borderRadius: 2 }}>
                      <Typography variant="h4" sx={{ fontWeight: 800, color }}>{value}</Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>{label}</Typography>
                    </Paper>
                  </Grid>
                ))}
              </Grid>
              <Divider sx={{ mb: 2 }} />
              <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 2 }}>Question Details</Typography>
              {selectedSwapeSession.answers && selectedSwapeSession.answers.length > 0 ? (
                <TableContainer component={Paper} sx={{ borderRadius: 2, maxHeight: 420 }} variant="outlined">
                  <Table size="small" stickyHeader>
                    <TableHead>
                      <TableRow>
                        {["#", "Question", "Selected", "Correct", "Status", "AI Explanation"].map((h) => (
                          <TableCell key={h} sx={{ fontWeight: 700, bgcolor: "#f8f9fb" }}>{h}</TableCell>
                        ))}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {selectedSwapeSession.answers.map((answer, idx) => (
                        <TableRow key={answer.question_id || idx}>
                          <TableCell>{answer.question_number || idx + 1}</TableCell>
                          <TableCell sx={{ maxWidth: 280 }}>
                            <Typography variant="body2" noWrap title={answer.question_text}>{answer.question_text || "—"}</Typography>
                            {renderQuestionVisual(answer, `modal-${answer.question_id || idx}`)}
                          </TableCell>
                          <TableCell>
                            {answer.answer_type === "vocab_matching" && answer.matched_pairs
                              ? <Chip size="small" label={`${answer.pairs_correct ?? 0}/${answer.pairs_total ?? 0} pairs`} color={answer.pairs_correct === answer.pairs_total ? "success" : "warning"} />
                              : answer.answer_type && answer.answer_type !== "mcq"
                                ? <Typography variant="body2" noWrap title={answer.text_answer} sx={{ maxWidth: 200 }}>{answer.text_answer || "—"}</Typography>
                                : (answer.selected_letter ? `${answer.selected_letter}${answer.selected_option ? ` (${answer.selected_option})` : ""}` : "—")
                            }
                          </TableCell>
                          <TableCell>
                            {answer.answer_type === "vocab_matching" && answer.matched_pairs
                              ? <Typography variant="body2">{answer.pairs_total ?? 0} pairs</Typography>
                              : answer.answer_type && answer.answer_type !== "mcq"
                                ? <Typography variant="body2" noWrap title={answer.correct_text} sx={{ maxWidth: 200 }}>{answer.correct_text || "—"}</Typography>
                                : (answer.correct_letter ? `${answer.correct_letter}${answer.correct_option ? ` (${answer.correct_option})` : ""}` : "—")
                            }
                          </TableCell>
                          <TableCell>
                            <Chip size="small"
                              label={answer.is_correct === null ? (answer.attempted ? "Submitted" : "—") : answer.is_correct ? "Correct" : "Incorrect"}
                              color={answer.is_correct === null ? "warning" : answer.is_correct ? "success" : "error"}
                              sx={{ fontWeight: 700 }}
                            />
                          </TableCell>
                          <TableCell sx={{ minWidth: 280, maxWidth: 400 }}>
                            {answer.explanation || answer.explanation_video_url ? (
                              <Box>
                                {answer.explanation && (
                                  <Box sx={{ color: "text.secondary", fontSize: "0.875rem", lineHeight: 1.43, "& p": { m: 0 } }}>
                                    <HtmlWithLatex html={answer.explanation} />
                                  </Box>
                                )}
                                {answer.explanation_video_url && <Box sx={{ mt: 1 }}><VideoExplanationPlayer videoUrl={makeAbsoluteUrl(answer.explanation_video_url)} questionText={answer.question_text} variant="button" /></Box>}
                              </Box>
                            ) : (
                              <Typography variant="body2" color="text.secondary" fontStyle="italic">No explanation available</Typography>
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              ) : (
                <Alert severity="info">No answer details available for this Sweep session.</Alert>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={handleCloseSwapeModal} variant="contained" sx={{ borderRadius: 2, fontWeight: 700 }}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* ── SWEEP CONFIRMATION ────────────────────────────────────────────── */}
      <Dialog open={swapeConfirmOpen} onClose={handleCancelSwape} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>Confirm Sweep Session</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2, mt: 1, borderRadius: 2 }}>
            You have limited Sweep sessions per upload. Once started, this session counts towards your limit.
          </Alert>
          {pendingSwapeAttempt && (() => {
            const stats = getAttemptStats(pendingSwapeAttempt);
            const uploadData = retakeHistory?.by_upload_attempt?.find((u) => u.upload_attempt_count === pendingSwapeAttempt.attempt_count);
            const swapeSessionsRemaining = uploadData?.swape_sessions_remaining ?? 4;
            const maxSwapes = retakeHistory?.max_swapes_per_upload ?? 4;
            return (
              <>
                <Typography sx={{ mb: 1 }}>
                  Start a Sweep for <strong>{stats.incorrectAnswers}</strong> incorrect question{stats.incorrectAnswers !== 1 ? "s" : ""}?
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Sessions remaining: <strong>{swapeSessionsRemaining}</strong> of {maxSwapes}
                </Typography>
              </>
            );
          })()}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
          <Button onClick={handleCancelSwape} sx={{ borderRadius: 2 }}>Cancel</Button>
          <Button onClick={handleConfirmSwape} variant="contained" color="warning" sx={{ borderRadius: 2, fontWeight: 700 }}>Confirm Sweep</Button>
        </DialogActions>
      </Dialog>
      {canVerifyWorksheet && (
        <Dialog open={wsSheetVerifyDialogOpen} onClose={() => setWsSheetVerifyDialogOpen(false)} maxWidth="xs" fullWidth>
          <DialogTitle sx={{ fontWeight: 700 }}>Verify Worksheet Grading</DialogTitle>
          <DialogContent dividers>
            {result?.id && getWSSheetItemData("worksheet", result.id)?.aggregate?.total > 0 && (
              <Box sx={{ mb: 2 }}>
                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                  Other testers ({getWSSheetItemData("worksheet", result.id).aggregate.total})
                </Typography>
                <Box sx={{ display: "flex", gap: 1, mt: 0.5, flexWrap: "wrap" }}>
                  {getWSSheetItemData("worksheet", result.id).aggregate.approved > 0 && (
                    <Chip label={`${getWSSheetItemData("worksheet", result.id).aggregate.approved} Approved`} color="success" size="small" variant="outlined" />
                  )}
                  {getWSSheetItemData("worksheet", result.id).aggregate.rejected > 0 && (
                    <Chip label={`${getWSSheetItemData("worksheet", result.id).aggregate.rejected} Rejected`} color="error" size="small" variant="outlined" />
                  )}
                  {getWSSheetItemData("worksheet", result.id).aggregate.flagged > 0 && (
                    <Chip label={`${getWSSheetItemData("worksheet", result.id).aggregate.flagged} Flagged`} color="warning" size="small" variant="outlined" />
                  )}
                </Box>
              </Box>
            )}
            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>Your verdict on AI grading accuracy</Typography>
            <RadioGroup row value={wsVerifyStatus} onChange={(e) => setWsVerifyStatus(e.target.value)} sx={{ mt: 0.5 }}>
              <FormControlLabel value="approved" control={<Radio size="small" color="success" />} label="Approved" />
              <FormControlLabel value="rejected" control={<Radio size="small" color="error" />} label="Rejected" />
              <FormControlLabel value="flagged" control={<Radio size="small" color="warning" />} label="Flagged" />
            </RadioGroup>
            <TextField label="Notes (optional)" multiline minRows={2} fullWidth size="small" value={wsVerifyNotes} onChange={(e) => setWsVerifyNotes(e.target.value)} sx={{ mt: 1 }} />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setWsSheetVerifyDialogOpen(false)} disabled={wsSheetVerifySubmitting}>Cancel</Button>
            <Button
              variant="contained"
              color={wsVerifyStatus === "approved" ? "success" : wsVerifyStatus === "rejected" ? "error" : "warning"}
              disabled={wsSheetVerifySubmitting || !result?.id}
              startIcon={wsSheetVerifySubmitting ? <CircularProgress size={16} /> : null}
              onClick={async () => {
                const ok = await submitWSSheetVerification({ type: "worksheet", id: result.id, status: wsVerifyStatus, notes: wsVerifyNotes });
                if (ok) setWsSheetVerifyDialogOpen(false);
              }}
            >
              {wsSheetVerifySubmitting ? "Saving…" : `Submit (${wsVerifyStatus.charAt(0).toUpperCase() + wsVerifyStatus.slice(1)})`}
            </Button>
          </DialogActions>
        </Dialog>
      )}
    </Box>
  );
};

export default AiAnswerSheetUpload;
